import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  CarriersModuleRights,
  CommitsModuleRights,
  ConfigurationRights,
  DashboardsRights,
  DateTypeRights,
  FinancialModuleRights,
  ShipmentsModuleRights,
  SlotsModuleRights,
  SlotsStorageModuleRights,
  StocksModuleRights,
  SupplyVisibilityRights,
  UserRights,
  VendorListRights,
} from '@orion-platform/core';
import { Column, ColumnData } from '@orion-platform/datagrid';
import {
  Commit,
  CommonSvVisualization,
  DummyCommitDetail,
  SecurityLevelData,
} from 'apps/orion/src/app/features/scm/material-management/v1/models/supply-visibility.model';
import { plainToInstance } from 'class-transformer';
import { NotificationService } from 'libs/core/src/lib/services';
import {
  extractApiErrorMessage,
  isErrorHandledByInterceptor,
  isNetworkError,
} from 'libs/core/src/lib/utils';
import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import moment, { Moment } from 'moment';
import { firstValueFrom } from 'rxjs';
import {
  CarriersListField,
  EnumCarriersFieldStructure,
} from '../features/scm/material-management/v1/models/carriers-list.model';
import {
  CommitProperty,
  CommonCommitFields,
} from '../features/scm/material-management/v1/models/commit.model';
import {
  EnumGoodsReceiptsFieldStructure,
  GoodsReceiptsList,
} from '../features/scm/material-management/v1/models/goods-receipts.model';
import {
  EnumInvoicingFieldStructure,
  InvoicingList,
} from '../features/scm/material-management/v1/models/invoicing.model';
import {
  EnumPartNumbersFieldStructure,
  PartNumbersListField,
} from '../features/scm/material-management/v1/models/partnumbers-list.model';
import {
  EnumPortfolioFieldStructure,
  PortfolioListField,
} from '../features/scm/material-management/v1/models/portfolio.model';
import {
  EnumShipmentFieldStructure,
  ShipmentListField,
} from '../features/scm/material-management/v1/models/shipments.model';
import {
  EnumSlotsStorageFieldStructure,
  SlotsStorageField,
} from '../features/scm/material-management/v1/models/slots-storage.model';
import {
  EnumSlotsFieldStructure,
  SlotsField,
} from '../features/scm/material-management/v1/models/slots.model';
import {
  EnumStocksFieldStructure,
  StocksListField,
} from '../features/scm/material-management/v1/models/stocks-list.model';
import { BatchEditCommits } from '../features/scm/material-management/v1/models/supply-visibility.model';
import {
  DashboardType,
  SortCacheValue,
} from '../features/scm/material-management/v1/models/sv-dashboard';
import {
  EnumVendorsFieldStructure,
  VendorsListField,
} from '../features/scm/material-management/v1/models/vendors-list.model';
import { PnVcFlagsHelperModel } from '../features/scm/material-management/v1/models/virtual-pn-groups.model';
import {
  AddUserSettings,
  DownloadNotificationFile,
  EditUserSettings,
  GetDefaultFieldsSuccess,
  HandleLinkEvents,
} from '../features/scm/material-management/v1/store/supply-visibility/supply-visibility.actions';
import { FieldsData } from '../features/scm/material-management/v1/store/user-settings/user-settings.types';
import { ApiQueryParams, SortItem } from '../models/app-config.model';
import {
  CommitVisualization,
  DateRangeParameters,
  EnumCommitFieldStructure,
  EnumSvSidebarSection,
  FieldVisibilitySetting,
  PnVendorCode,
  UserSettings,
  UserSettingsCls,
} from '../models/supply-visibility.model';

type dateFields =
  | 'actualETADate'
  | 'arrivalDate'
  | 'codeDate'
  | 'createDate'
  | 'deliveryDate'
  | 'eddDate'
  | 'etaDate'
  | 'etaPortDate'
  | 'etdDate'
  | 'expirationDate'
  | 'inboundDeliveryDate'
  | 'instructionEtaDate'
  | 'lastUpdateDate'
  | 'otmReceivedDate'
  | 'plannedOrderStatus'
  | 'purchasingDocumentDate'
  | 'receiveDate'
  | 'recomitRequestDate'
  | 'requestDate'
  | 'sapDeliveryDate'
  | 'scheduleLineDate'
  | 'slotDate'
  | 'triggerDate';

export class Helper {
  // public static readonly REVIEW_FLAG_KEY: string = 'Review';
  public static readonly REVIEW_BUYER_FLAG: string = 'ReviewByBuyer';
  public static readonly REVIEW_SUPPLIER_FLAG: string = 'ReviewBySupplier';
  public static readonly REVIEW_CUSTOMER_FLAG: string = 'ReviewByCustomer';
  public static readonly SV_MODULE: string = 'sv-module';
  public static readonly COMMIT_MODULE: string = 'commits-module';
  public static readonly VENDORS_MODULE: string = 'vendors-module';
  public static readonly MYPORTFOLIO_MODULE: string = 'myPortFolio-module';
  public static readonly MMVIEWS_MODULE: string = 'MMViews-module';
  public static readonly CONTACTS_MODULE: string = 'contacts-module';
  public static readonly PNGROUP_MODULE: string = 'pn-groups-module';
  public static readonly QUOTATIONS_MODULE: string = 'quotations-module';
  public static readonly CARRIERS_MODULE: string = 'carriers-module';
  public static readonly STOCKS_MODULE: string = 'stocks-module';
  public static readonly GOODRECEIPTS_MODULE: string = 'goodReceipts-module';
  public static readonly INVOICING_MODULE: string = 'invoicing-module';
  public static readonly FINANCIAL_MODULE: string = 'financial-module';
  public static readonly ACTIVITY_MODULE: string = 'activity-module';
  public static readonly NOTES_AND_COMMENTS_MODULE: string = 'notes-and-comments-module';
  public static readonly PARTNUMBERS_MODULE: string = 'partnumbers-module';
  public static readonly QAP_MODULE: string = 'qap-module';
  public static readonly PORTFOLIO_MODULE: string = 'portfolio-module';
  public static readonly GLOBALNOTIFICATIONS_MODULE: string = 'globalNotifications-module';
  public static readonly SHIPMENTS_MODULE: string = 'shipments-module';
  public static readonly VENDORCODE_RIGHTS_MODULE: string = 'vendorcodes-rights-module';
  public static readonly SHIPMENTS_UPDATE_MODULE: string = 'shipments-update-module';
  public static readonly UPLOAD_DOCUMENTS_MODULE: string = 'upload-documents-module';
  public static readonly INSTRUCTIONS_MODULE: string = 'instructions-module';
  public static readonly SLOTS_STORAGE_MODULE: string = 'slots-storage-module';
  public static readonly SLOT_CALENDAR_MODULE: string = 'slot-calendar-module';
  public static readonly SLOTS_MODULE: string = 'slots-module';

  // formating for input and prepare data for calling api as array param
  static formatPasteList(origintext: string, clipboardtext: string): string {
    const originText = origintext || '';
    const modifiedText = (clipboardtext || '')
      // Normalize all separators (tabs, newlines, semicolons, commas) into a single comma
      .replace(/[\t\n\r;,]+/g, ',')
      .split(',')
      // Trim whitespace and strip surrounding quotes (e.g. values pasted from Excel, including smart quotes)
      .map((item): string => item.trim().replace(/^["\u201c\u201d']+|["\u201c\u201d']+$/g, ''))
      // Remove empty entries
      .filter((item): boolean => item.length > 0)
      .join(',');

    return modifiedText ? originText + modifiedText : originText;
  }

  static trimLeadingZeros(number: string): string {
    try {
      return number.replace(/^0+/, '');
    } catch {
      return number;
    }
  }

  public static getCommitHistoryDateRange(): DateRangeParameters {
    const dateFrom = moment().subtract(14, 'days');
    const dateTo = moment().add(6, 'months');

    const dateRange: DateRangeParameters = {
      dateFrom: dateFrom.toDate(),
      dateTo: dateTo.toDate(),
    };
    return dateRange;
  }

  public static getCommitFilterDateRange(): DateRangeParameters {
    const dateFrom = moment().subtract(1, 'month');
    const dateTo = moment().add(1, 'month');

    const dateRange: DateRangeParameters = {
      dateFrom: dateFrom.toDate(),
      dateTo: dateTo.toDate(),
    };
    return dateRange;
  }

  public static getDefaultCommonSvVisualization(): CommonSvVisualization {
    const defaultCommonSvVisualization: CommonSvVisualization = {
      svSidebarOpened: false,
      svSidebarOpenedSection: EnumSvSidebarSection.None,
      svSplitAreaSize: { mainView: 80, sidebar: 20 },
    };
    return defaultCommonSvVisualization;
  }

  public static getInputDateFormats(): string[] {
    return ['YYYY-MM-DD', 'YYYY.MM.DD', 'YYYY/MM/DD', 'DD.MM.YYYY', 'DD-MM-YYYY', 'DD/MM/YYYY'];
  }

  public static getLinkEventRegex(isHandleLink?: boolean): RegExp {
    return new RegExp(isHandleLink ? 'hdl-[a-z]+:[0-9]+' : '#[a-z]+:[0-9]+', 'g');
  }

  public static getNumberRegex(isEmpty?: boolean): RegExp {
    return new RegExp(`[0-9]${isEmpty ? '' : '+'}`, 'g');
  }

  public static addLinkEventsOnHtmlString(message: string): string {
    const eventRegex = this.getLinkEventRegex();
    const eventValueRegex = this.getNumberRegex();

    const unformattedEvents = message.match(eventRegex) ?? [];

    for (let i = 0; i < unformattedEvents.length; i++) {
      const event = unformattedEvents[i];
      const eventValue = event.match(eventValueRegex) ?? [];

      if (eventValue.length) {
        // Skip quotations
        if (event.startsWith('#quotation:')) continue;

        const anchor = this.generateAnchorTagString(
          eventValue[0] ?? '',
          event.replace('#', 'hdl-'),
          i.toString(),
        );
        message = message.replace(event, anchor);
      }
    }

    message = this.addErrorToMessage(message);
    return `<span id="linkEvents" style="display:none;">${message}</span>`;
  }

  public static addErrorToMessage(message: string): string {
    const replaceWithError = (part: string): string => {
      const regex = /\*error/g;
      part = part.replace(regex, '');
      return `<span class="error-notification" style="color:red;">${part}</span>`;
    };

    const messageParts = message.split('<br>');
    messageParts.forEach((part, index): void => {
      if (this.notificationHasError(part)) {
        messageParts[index] = replaceWithError(part);
      }
    });

    return messageParts.join('<br>');
  }

  public static notificationHasError(message: string): boolean {
    return message.includes('*error');
  }

  public static setClickEventsOnInnerTemplate(
    store: Store,
    documentRef: Document,
    elRef: ElementRef,
    renderer: Renderer2,
  ): Array<() => void> {
    const rendererListeners: Array<() => void> = [];

    const parentLinkContainer: HTMLElement | null = documentRef.getElementById('parentLinkEvents');
    if (!parentLinkContainer) {
      return rendererListeners;
    }

    const allContent: HTMLElement | null = elRef.nativeElement.querySelector('#linkEvents');
    const parentContainer: HTMLElement | null =
      elRef.nativeElement.querySelector('#parentLinkEvents');

    if (!allContent || !parentContainer) {
      return rendererListeners;
    }

    renderer.appendChild(parentContainer, allContent);
    renderer.removeStyle(allContent, 'display');

    const clickButtons: NodeListOf<Element> =
      elRef.nativeElement.querySelectorAll('.clickHandleAnchor');

    for (let i = 0; i < clickButtons.length; i += 1) {
      let disableClick = false;

      const rendererListener: () => void = renderer.listen(
        clickButtons[i],
        'click',
        ($event: Event): void => {
          if (disableClick) {
            return;
          }

          disableClick = true;

          const target = $event.target as HTMLElement | null;
          const eventData: string | undefined = target?.dataset?.['value'];

          if (eventData) {
            store.dispatch(new HandleLinkEvents(eventData));
          }

          setTimeout((): void => {
            disableClick = false;
          }, 3000);
        },
      );

      rendererListeners.push(rendererListener);
    }

    // Real file links (e.g. ".../api/v1/file/...") in the notification message must be
    // downloaded via API call instead of letting the browser navigate to the raw endpoint.
    const fileLinks: NodeListOf<HTMLAnchorElement> =
      elRef.nativeElement.querySelectorAll('a[href]');

    for (let i = 0; i < fileLinks.length; i += 1) {
      const anchor = fileLinks[i];
      if (!this.isFileDownloadLink(anchor.getAttribute('href'))) {
        continue;
      }

      const rendererListener: () => void = renderer.listen(
        anchor,
        'click',
        ($event: Event): void => {
          $event.preventDefault();
          const url = anchor.getAttribute('href');
          if (url) {
            store.dispatch(new DownloadNotificationFile(url));
          }
        },
      );

      rendererListeners.push(rendererListener);
    }

    return rendererListeners;
  }

  public static isFileDownloadLink(href: null | string | undefined): boolean {
    return !!href && href.includes('/api/v1/file/');
  }

  public static pnVcFlags(): PnVcFlagsHelperModel[] {
    return [
      {
        controlType: 'simple',
        field: 'partNumber',
        headerText: 'PartNumber',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'vendorCode',
        headerText: 'VendorCode',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'flag',
        headerText: 'Flag',
        visible: true,
      },
    ];
  }

  public static shipmentFlags(): PnVcFlagsHelperModel[] {
    return [
      {
        controlType: 'simple',
        field: 'id',
        headerText: 'ID',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'flag',
        headerText: 'Flag',
        visible: true,
      },
    ];
  }

  // public static getNotesToolbarItems() {
  //   return [
  //     {
  //       acceptedValues: [],
  //       name: 'bold',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'italic',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'strike',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'underline',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'clear',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: ['7pt', '8pt', '9pt', '11pt', '11pt', '12pt', '13pt', '14pt', '15pt'],
  //       name: 'size',
  //       options: null,
  //       type: 'acceptedValuess',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'color',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'background',
  //       options: { icon: 'fill' },
  //       type: 'options',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [false, 1, 2, 3, 4, 5],
  //       name: 'header',
  //       options: null,
  //       type: 'acceptedValuess',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'codeBlock',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'blockquote',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: ['left', 'center', 'right', 'justify'],
  //       name: 'align',
  //       options: null,
  //       type: 'acceptedValuess',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'orderedList',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'bulletList',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'link',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'undo',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'redo',
  //       options: null,
  //       type: 'simple',
  //     },
  //     {
  //       acceptedValues: [],
  //       name: 'separator',
  //       options: null,
  //       type: 'simple',
  //     },
  //   ];
  // }

  public static getContactListForFields(): MappedField[] {
    return [
      {
        controlType: 'simple',
        field: 'street',
        headerText: 'Street',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'city',
        headerText: 'City',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'postCode',
        headerText: 'PostCode',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'country',
        headerText: 'Country',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'state',
        headerText: 'State',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'phone',
        headerText: 'Phone',
        visible: true,
      },
      {
        controlType: 'simple',
        field: 'email',
        headerText: 'Email',
        visible: true,
      },
    ];
  }

  // do not use this method for larger arrays
  public static compareArrayObjects<T extends Record<string, unknown>>(
    arrObj1: T[],
    arrObj2: T[],
    ignoreKeys?: Array<keyof T>,
  ): boolean {
    if ((!arrObj1 && arrObj2) || (arrObj1 && !arrObj2)) {
      return false;
    }
    if (!arrObj1 && !arrObj2) {
      return true;
    }
    if (arrObj1.length != arrObj2.length) {
      return false;
    }

    arrObj1 = this.createCopy(arrObj1);
    arrObj2 = this.createCopy(arrObj2);

    for (let i = 0; i < arrObj1.length; i++) {
      if (ignoreKeys) {
        for (let j = 0; j < ignoreKeys.length; j++) {
          delete arrObj1[i][ignoreKeys[j]];
          delete arrObj2[i][ignoreKeys[j]];
        }
      }
      const index = arrObj2.findIndex((element): boolean =>
        this.compareObjects(element, arrObj1[i]),
      );
      if (index == -1) {
        return false;
      }
    }
    return true;
  }

  /**
   * For destroying/unlisten any elements in renderer
   * @returns void
   */
  public static destroyRendererListener(rendererListeners: Array<() => void>): void {
    for (let i = 0; i < rendererListeners.length; i++) {
      rendererListeners[i]();
    }
  }

  public static generateAnchorTagString(
    text: string,
    value: string,
    id: string,
    hrefLink?: string,
  ): string {
    //RND For Normal Tag With Style
    return ` <a class="clickHandleAnchor" id="${id}" href="${
      hrefLink ? hrefLink : 'javascript:void(0)'
    }" target="_self" data-value='${value}'>${text}</a> `; // do not remove space
  }

  public static detectChanges(ref: ChangeDetectorRef): void {
    ref.detectChanges();
  }
  //----------Invalid/Hidden Data Sample Start
  public static readonly invalidDates: string[] = ['1/1/1990', '1/1/1900', '1/1/2001', '1/1/0001'];
  public static readonly invalidInvoiceNo: string[] = ['NULL INVOICE'];

  //----------Invalid/Hidden Data Sample End

  public static openInNewTab(url: string, router: Router): void {
    const baseUrl = window.location.origin;
    const fullUrl = router.serializeUrl(router.createUrlTree([url]));
    window.open(baseUrl + fullUrl, '_blank');
  }

  public static getUrlPaths(): string {
    const parsedUrl = new URL(window.location.href);
    const path = parsedUrl.pathname + parsedUrl.search;
    return path;
  }

  /**
   *
   * @param error
   * @returns prints deep error info
   */
  public static printError(error: unknown, title?: string): void {
    console.warn('**********Attention**************');
    title ? console.warn(`***********${title}***********`) : null;
    error ? console.error(error) : null;
  }

  public static securityLevelData: SecurityLevelData[] = [
    { id: 0, label: 'mmViews.protected' },
    { id: 1, label: 'mmViews.internal' },
    { id: 2, label: 'mmViews.customer' },
    { id: 3, label: 'mmViews.supplier' },
    { id: 4, label: 'Orion_SCM_Analyst' },
    { id: 5, label: 'Orion_SCM_Buyer' },
    { id: 6, label: 'Orion_SCM_Supplier_Commit' },
    { id: 7, label: 'Orion_SCM_Supplier_No_Commit' },
    { id: 8, label: 'Orion_SCM_Supplier_ROP' },
    { id: 9, label: 'Orion_SCM_Customer' },
    { id: 10, label: 'Orion_SCM_ReadOnly' },
    { id: 11, label: 'Orion_Logistics_Logistic' },
    { id: 12, label: 'Orion_SCM_Engineering' },
    { id: 13, label: 'Orion_SCM_Customer_Commit' },
    { id: 14, label: 'Orion_SCM_Sourcing' },
    //{ id: 15, label: 'Orion_Administrator' },
    { id: 16, label: 'Orion_SCM_Master_Buyer' },
    { id: 19, label: 'Orion_Logistics_Carrier' },
    { id: 20, label: 'Orion_Logistics_Analyst' },
    { id: 21, label: 'Orion_Logistics_Warehouse' },
    { id: 22, label: 'Orion_Logistics_Declarant' },
    { id: 23, label: 'Orion_Finance_Accountant' },
    { id: 24, label: 'Orion_Finance_Analyst' },
    { id: 25, label: 'Orion_Logistics_Readonly' },
    { id: 26, label: 'Orion_Warehouse_Analyst' },
    { id: 27, label: 'Orion_Logistics_Slot' },
    { id: 28, label: 'Orion_SCM_Supplier_BS' },
  ];

  public static splitVendorCodes(vendorCodes: null | string | undefined): string[] {
    return vendorCodes
      ? Helper.virtualVCReplace(vendorCodes)
          .split(/,|;|\s+/gm)
          .map((c): string => (c ? c.trim() : ''))
          .filter((c): string => c)
      : [];
  }

  private static readonly virtualVCRegex = new RegExp('VIRTUALVC', 'g');

  public static virtualVCReplace(pnVC: string): string {
    let pnVCItems = pnVC.split(';');
    pnVCItems = pnVCItems.map((pnVCItem): string => {
      const pnVCSplit = pnVCItem.split('|');
      return pnVCSplit.length < 2
        ? pnVCItem
        : pnVCItem
            .replace(pnVCSplit[1], pnVCSplit[1].toUpperCase())
            .replace(this.virtualVCRegex, 'VirtualVC');
    });
    return pnVCItems.join(';');
  }

  public static readonly commitFilterDates: CommitProperty[] = [
    { id: 'sapDeliveryDate', name: 'Final ETA Date', type: 'date' }, // Changed as per skype request
    { id: 'etaDate', name: 'ETA Date', type: 'date' },
    { id: 'deliveryDate', name: 'Delivery Date', type: 'date' },
    { id: 'etdDate', name: 'ETD Date', type: 'date' },
    { id: 'slotDate', name: 'Slot Date', type: 'date' },
    { id: 'requestDate', name: 'Request Date', type: 'date' },
    { id: 'triggerDate', name: 'Trigger Date', type: 'date' },
    { id: 'recomitRequestDate', name: 'Recomit Request Date', type: 'date' },
    { id: 'eddDate', name: 'EDD Date', type: 'date' },
    // { id: "currentAGEDDate", name: "Current AGED Date", type: "date" }, //Removed as per skype request
    { id: 'instructionEtaDate', name: 'Instruction ETA Date', type: 'date' },
    { id: 'otmReceivedDate', name: 'Otm Received Date', type: 'date' },
    { id: 'actualETADate', name: 'Actual ETA Date', type: 'date' },
    { id: 'receiveDate', name: 'Receipt Date', type: 'date' },
    { id: 'plannedOrderStatus', name: 'Open Orders Date', type: 'date' },
    { id: 'createDate', name: 'Created Date', type: 'date' },
  ];

  public static getCommitFilterDates(defaultFields?: string[]): CommitProperty[] {
    if (defaultFields) {
      const dateFields = this.commitFilterDates.filter((obj): boolean =>
        defaultFields.includes(obj.id as string),
      );
      return dateFields;
    }
    return [];
  }

  static readonly dateFields: dateFields[] = [
    'actualETADate',
    'arrivalDate',
    'codeDate',
    'createDate',
    'deliveryDate',
    'eddDate',
    'etaDate',
    'etaPortDate',
    'etdDate',
    'expirationDate',
    'inboundDeliveryDate',
    'instructionEtaDate',
    'lastUpdateDate',
    'otmReceivedDate',
    'plannedOrderStatus',
    'purchasingDocumentDate',
    'receiveDate',
    'recomitRequestDate',
    'requestDate',
    'sapDeliveryDate',
    'scheduleLineDate',
    'slotDate',
    'triggerDate',
  ];

  public static formatCommitDate<T extends Commit | DummyCommitDetail>(commit: T): T {
    const dateTimePickerFields: string[] = ['arrivalDate'];
    const dateFields = this.dateFields.filter((field): boolean => {
      return !dateTimePickerFields.includes(field);
    });

    const commitRecord = commit as Record<string, unknown>;
    for (const field of dateFields) {
      const value = commitRecord[field];

      if (!value) {
        continue;
      }

      if (field === 'slotDate') {
        commitRecord[field] = this.formatUTCDateTime(value as string);
      } else {
        commitRecord[field] = this.formatDate(value as string);
      }
    }
    return commitRecord as T;
  }

  public static formatBatchCommitDate(filter: BatchEditCommits): BatchEditCommits {
    const dateTimePickerDates: string[] = ['SlotDate', 'ArrivalDate'];
    const keys = dateTimePickerDates.map((v): string => v.toLowerCase());

    filter.fields.forEach((element): void => {
      //Komponenta pro výběr data používá MomentDateAdapter
      if (moment.isMoment(element.value)) {
        element.value = (element.value as Moment).toISOString();
      }

      const value = element.value;

      if (value === null || value === undefined || value === '') {
        element.value = null;
        return;
      }

      if (typeof value !== 'string') {
        return;
      }

      if (keys.includes(element.key.toLowerCase())) {
        element.value = this.formatUTCDateTime(element.value as string);
      } else {
        const lowerCaseDateFields: string[] = this.dateFields.map((v): string => v.toLowerCase());
        if (lowerCaseDateFields.includes(element.key.toLowerCase())) {
          element.value = this.formatDate(element.value as string);
        }
      }
    });
    return filter;
  }

  public static formatUTCDateTime(value: string): Moment | string {
    if (value && value != 'Invalid date')
      try {
        return moment(value).utc();
      } catch (e) {
        console.error(e);
        return value;
      }
    return value;
  }

  public static formatDateWithTime(value: Date | null | number | string | undefined): string {
    if (value) {
      try {
        return moment(value).format('YYYY-MM-DD HH:mm:ss');
      } catch (e) {
        console.error(e);
        return String(value);
      }
    }
    return value ? String(value) : '';
  }

  public static formatDate(value: Date | string): string {
    if (value && value != 'Invalid date') {
      try {
        return moment(value).format('YYYY-MM-DD');
      } catch (e) {
        console.error(e);
        return value.toString();
      }
    }
    return value?.toString() ?? '';
  }

  public static getSortedByPnVc<T extends Record<string, unknown>>(
    sort: null | SortCacheValue,
    data: T[],
  ): T[] {
    if (data) {
      let sortedData = [...data];

      if (sort) {
        if (sort.key === 'virtualPN') {
          sortedData = sortedData.sort((a, b): number => {
            const itemA = a as unknown as { subItems: string; vendorCode: string };
            const itemB = b as unknown as { subItems: string; vendorCode: string };

            if (sort.type === 'ascending') {
              if (itemA.subItems === itemB.subItems) {
                return (itemA.vendorCode || '').localeCompare(itemB.vendorCode || '');
              }
              return (itemA.subItems || '').localeCompare(itemB.subItems || '');
            } else if (sort.type === 'descending') {
              if (itemA.subItems === itemB.subItems) {
                return (itemB.vendorCode || '').localeCompare(itemA.vendorCode || '');
              }
              return (itemB.subItems || '').localeCompare(itemA.subItems || '');
            }
            return 0;
          });
        } else {
          sortedData = sortedData.sort((a, b): number => {
            const valA = a[sort.key as string] as null | number | string | undefined;
            const valB = b[sort.key as string] as null | number | string | undefined;

            // Handle null/undefined values
            const valueA = valA ?? '';
            const valueB = valB ?? '';

            if (sort.type === 'ascending') {
              return valueA > valueB ? 1 : -1;
            } else {
              return valueA < valueB ? 1 : -1;
            }
          });
        }
      } else {
        sortedData = sortedData.sort((a, b): number =>
          a['index'] && b['index']
            ? a['index'] < b['index']
              ? -1
              : a['index'] > b['index']
                ? 1
                : 0
            : 0,
        );
      }
      return sortedData;
    }
    return [];
  }

  public static modifiedPaginationSorting(params: ApiQueryParams): ApiQueryParams {
    const sortingData = params.sort;

    if (Array.isArray(sortingData)) {
      const resultString = sortingData
        .map((item): string => `${item.selector} ${item.desc ? 'desc' : 'asc'}`)
        .join(',');

      params.sort = resultString;
    } else if (typeof sortingData === 'object' && sortingData !== null) {
      const item = sortingData as SortItem;
      params.sort = `${item.selector} ${item.desc ? 'desc' : 'asc'}`;
    }

    return params;
  }

  public static showError(
    notificationService: NotificationService,
    error: HttpErrorResponse,
    msg: string,
  ): void {
    if (isNetworkError(error) || isErrorHandledByInterceptor(error)) return;
    notificationService.showError(extractApiErrorMessage(error) ?? msg);
  }

  /**
   *
   * @param objectToCopy
   * @returns gives object deepCopy
   */
  // public static createCopy(objectToCopy: unknown): unknown {
  //   return JSON.parse(JSON.stringify(objectToCopy));
  // }
  public static createCopy<T>(objectToCopy: T): T {
    return JSON.parse(JSON.stringify(objectToCopy));
  }

  public static getWidgetAccess(widgetList: string[], data: DashboardType[]): DashboardType[] {
    if (widgetList && data) {
      const filteredWidgetData = data.filter((item): boolean =>
        widgetList.includes(item.type as string),
      );
      return filteredWidgetData;
    } else {
      return [];
    }
  }

  /**
   * do not use for large arrays
   * @param compareArray
   * @returns array has same elements or not
   */
  public static compareArray(array1: number[] | string[], array2: number[] | string[]): boolean {
    let result = true;

    if (!(array1 && array2)) {
      return array1 == array2 ? true : false;
    }

    if (array1.length != array2.length || typeof array1 != typeof array2) {
      result = false;
      return result;
    }
    for (let i = 0; i < array1.length; i++) {
      const index = array2.findIndex((element): boolean => element == array1[i]);
      if (index == -1) {
        result = false;
        break;
      }
    }
    return result;
  }

  /**
   * @param Objects
   * @returns compares objects based on keys and values
   */
  public static compareObjects(obj1: unknown, obj2: unknown): boolean {
    if ((!obj1 && obj2) || (obj1 && !obj2)) {
      return false;
    }

    if (!obj1 && !obj2) {
      return true;
    }

    const o1 = obj1 as Record<string, unknown>;
    const o2 = obj2 as Record<string, unknown>;

    const obj1Keys = Object.keys(o1);
    const obj2Keys = Object.keys(o2);

    if (obj1Keys.length != obj2Keys.length || !this.compareArray(obj1Keys, obj2Keys)) {
      return false;
    }

    for (let i = 0; i < obj1Keys.length; i++) {
      if (o1[obj1Keys[i]] != o2[obj1Keys[i]]) {
        return false;
      }
    }
    return true;
  }

  public static setOrderByAlpha<T, K extends keyof T>(key: K, columns: T[]): T[] {
    const copiedColumns = [...columns];
    return copiedColumns.sort((a, b): number => {
      const textA = String(a[key]).toLowerCase();
      const textB = String(b[key]).toLowerCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });
  }

  public static readonly defaultCommitVisualization: CommitVisualization = {
    columnSort: [],
    pageSettings: { pageIndex: 0, pageSize: 10 },
  };

  public static saveSettings(
    store: Store,
    payload: { data: unknown; key: string },
    isNew = false,
    isNotificationRequired = false,
    updateIdInData = true,
  ): void {
    const changedData = {
      data: JSON.stringify(payload.data),
      key: payload.key,
    };
    if (isNew) {
      store.dispatch(new AddUserSettings(changedData, updateIdInData));
    } else {
      store.dispatch(new EditUserSettings(changedData, isNotificationRequired));
    }
  }

  public static setCheckColumn(columns: Column[]): Column[] {
    columns = [...columns];
    if (columns.find((column): boolean => column.field === 'isChecked')) {
      return columns;
    }
    const check = {
      field: 'isChecked',
      headerText: '',
      type: 'checkbox',
      visible: true,
    };
    columns.unshift(check);
    this.setIndexFieldToArray(columns);
    return columns;
  }

  public static removeCheckColumn(columns: Column[]): Column[] {
    columns = [...columns];
    if (columns) {
      const index = columns.findIndex((column): boolean => column.field == 'isChecked');
      if (index > -1) {
        columns.splice(index, 1);
      }
    }
    return columns;
  }

  public static removeEmptyColumns(columns: Array<{ field?: string }>): Array<{ field?: string }> {
    columns = columns.filter(
      (col): boolean | string | undefined => col.field && col.field.trim() !== '',
    );
    return columns;
  }

  public static saveFieldsVisibility(
    store: Store,
    key: string,
    columns: Column[],
    columnAlreadySaved: boolean,
    type:
      | EnumCarriersFieldStructure
      | EnumCommitFieldStructure
      | EnumGoodsReceiptsFieldStructure
      | EnumInvoicingFieldStructure
      | EnumPartNumbersFieldStructure
      | EnumPortfolioFieldStructure
      | EnumShipmentFieldStructure
      | EnumSlotsFieldStructure
      | EnumSlotsStorageFieldStructure
      | EnumStocksFieldStructure
      | EnumVendorsFieldStructure,
  ): void {
    console.log(key);
    const fieldVisibilitySettings: FieldVisibilitySetting[] = [];
    if (
      type == EnumCommitFieldStructure.CommitTable ||
      type == EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable ||
      type == EnumInvoicingFieldStructure.InvoicingTable ||
      type == EnumVendorsFieldStructure.VendorTable ||
      type == EnumCarriersFieldStructure.CarriersTable ||
      type == EnumPortfolioFieldStructure.PortfolioTable ||
      type == EnumPartNumbersFieldStructure.PartNumberTable ||
      type == EnumShipmentFieldStructure.ShipmentTable ||
      type == EnumStocksFieldStructure.StocksTable ||
      type == EnumSlotsStorageFieldStructure.SlotsStorageTable ||
      type == EnumSlotsFieldStructure.SlotsTable
    ) {
      for (let i = 0; i < columns.length; i++) {
        const setting = {
          field: columns[i].field,
          index: i,
          visible: columns[i].visible,
        };
        fieldVisibilitySettings.push(setting);
      }
    } else if (
      type == EnumCommitFieldStructure.CommitForm ||
      type == EnumVendorsFieldStructure.VendorCreateForm ||
      type == EnumCarriersFieldStructure.CarriersCreateForm ||
      type == EnumPortfolioFieldStructure.PortfolioCreateForm ||
      type == EnumStocksFieldStructure.StocksCreateForm ||
      type == EnumSlotsStorageFieldStructure.SlotsStorageCreateForm
    ) {
      for (let i = 0; i < columns.length; i++) {
        const setting = {
          field: columns[i].field,
          index: i,
          visible: columns[i].visible,
        };
        fieldVisibilitySettings.push(setting);
      }
    } else {
      // const columnsKeys = Object.keys(columns);
      // for (let i = 0; i < columnsKeys.length; i++) {
      //   const setting = {
      //     field: columnsKeys[i],
      //     visible: columns[columnsKeys[i]] ? columns[columnsKeys[i]].visible : false,
      //   };
      //   fieldVisibilitySettings.push(setting);
      // }
    }

    const settings = {
      data: JSON.stringify(fieldVisibilitySettings),
      key: key,
    };
    if (!columnAlreadySaved) {
      store.dispatch(new AddUserSettings(settings));
    } else {
      store.dispatch(new EditUserSettings(settings));
    }
  }

  public static setIndexFieldToArray<T extends Record<string, unknown>>(
    array: T[],
  ): Array<T & { index: number }> {
    array = [...array] as T[];
    return array.map((item, index): T & { index: number } => ({ ...item, index }));
  }

  /**
   * @param settings set saved settings from server
   * @param type set field type
   * @param customFields set use custom fields
   */
  public static async getFieldsVisibility(
    fieldsData: FieldsData,
    commitModuleRights?: CommitsModuleRights,
  ): Promise<{ columnAlreadySaved: boolean; columns: Column[] | MappedField[] }> {
    let columns: Column[] | MappedField[] = [];

    let columnAlreadySaved = false;
    try {
      //set default model
      //load settings from server

      //TODO GET DEFAULT LIST
      if (fieldsData.defaultFields) {
        fieldsData.store
          ? fieldsData.store.dispatch(new GetDefaultFieldsSuccess(fieldsData.defaultFields))
          : null;
        if (
          fieldsData.defaultFields &&
          fieldsData.baseModule == Helper.COMMIT_MODULE &&
          fieldsData.type == EnumCommitFieldStructure.CommitForm
        ) {
          for (let i = 0; i < fieldsData.defaultFields.length; i++) {
            if (fieldsData.defaultFields[i] == 'vendorcode') {
              fieldsData.defaultFields[i] = 'chooseVendor';
            } else if (fieldsData.defaultFields[i] == 'purchaseordernumber') {
              fieldsData.defaultFields[i] = 'poNumber';
            }
          }
          if (
            commitModuleRights == undefined ||
            (commitModuleRights && commitModuleRights.lockCommit)
          )
            fieldsData.defaultFields.push('withMerge'); //2571 comment
          //ADD more customization to commit form
        }
      }
      columns = this.setDefaultColumns(
        fieldsData.defaultFields,
        columns,
        fieldsData.type,
        fieldsData.baseModule,
      );

      let settings: undefined | UserSettings;
      if (fieldsData?.key) {
        try {
          settings = await firstValueFrom(fieldsData.service.getUserSettings(fieldsData.key), {
            defaultValue: undefined,
          });
        } catch (error: unknown) {
          const specializedError = error as HttpErrorResponse;
          // TODO - temporary solution remove after few days
          if (specializedError && fieldsData.defaultFields) {
            const unSortedColumns = Helper.createCopy(columns);
            columns = unSortedColumns.sort((a, b): number => {
              if (typeof a.field === 'string' && typeof b.field === 'string') {
                const fieldA = a.field.toLowerCase();
                const fieldB = b.field.toLowerCase();
                return (
                  fieldsData.defaultFields.indexOf(fieldA) -
                  fieldsData.defaultFields.indexOf(fieldB)
                );
              }
              return 0;
            });
          }

          if (specializedError.status === 404) {
            const fieldVisibilitySettings: FieldVisibilitySetting[] = [];
            for (let i = 0; i < columns.length; i++) {
              const setting = {
                field: columns[i].field,
                index: i,
                visible: columns[i].visible,
              };
              fieldVisibilitySettings.push(setting as FieldVisibilitySetting);
            }
            fieldsData.store?.dispatch(new AddUserSettings({ data: '[]', key: fieldsData.key }));

            columnAlreadySaved = true;
          } else {
            settings = undefined;
          }
        }
      }

      if (
        fieldsData.baseModule !== Helper.COMMIT_MODULE ||
        fieldsData.type !== EnumCommitFieldStructure.CommitTable ||
        commitModuleRights?.selectFields
      ) {
        const formatColumn = this.formatColumns(
          settings,
          columns,
          columnAlreadySaved,
          fieldsData.type,
          fieldsData.defaultFields,
          fieldsData.baseModule,
        );

        columns = formatColumn.columns;
        columnAlreadySaved = formatColumn.columnAlreadySaved;
      }

      if (commitModuleRights && !commitModuleRights.selectFields && fieldsData.defaultFields) {
        const unSortedColumns = Helper.createCopy(columns);
        columns = unSortedColumns.sort((a, b): number => {
          if (typeof a.field === 'string' && typeof b.field === 'string') {
            const fieldA = a.field.toLowerCase();
            const fieldB = b.field.toLowerCase();
            return (
              fieldsData.defaultFields.indexOf(fieldA) - fieldsData.defaultFields.indexOf(fieldB)
            );
          }
          return 0;
        });
      }
    } catch (error) {
      //something has error go with default settings
      console.error(error);
      //columns = Helper.setDefaultColumns(null, columns, fieldsData.type, fieldsData.baseModule);
    }

    return { columnAlreadySaved, columns };
  }

  private static setDefaultColumns(
    customFields: string[],
    columns: Column[],
    type:
      | EnumCarriersFieldStructure
      | EnumCommitFieldStructure
      | EnumGoodsReceiptsFieldStructure
      | EnumInvoicingFieldStructure
      | EnumPartNumbersFieldStructure
      | EnumPortfolioFieldStructure
      | EnumShipmentFieldStructure
      | EnumSlotsFieldStructure
      | EnumSlotsStorageFieldStructure
      | EnumStocksFieldStructure
      | EnumVendorsFieldStructure,
    baseModule?: string,
  ): Column[] {
    columns = [...columns];
    if (baseModule == Helper.COMMIT_MODULE || baseModule == Helper.SV_MODULE) {
      switch (type) {
        case EnumCommitFieldStructure.CommitTable:
          // columns = [...this.columnsSettings]; @deprecated
          columns = new CommonCommitFields(EnumCommitFieldStructure.CommitTable, customFields)
            .fields;
          break;
        case EnumCommitFieldStructure.CommitForm:
          // columns = [...this.fieldsSettings];
          columns = new CommonCommitFields(EnumCommitFieldStructure.CommitForm, customFields)
            .fields;
          break;
        case EnumCommitFieldStructure.BulkEditForm:
          // columns = [...this.bulkEditFieldsSettings];
          columns = new CommonCommitFields(EnumCommitFieldStructure.BulkEditForm, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.GOODRECEIPTS_MODULE) {
      switch (type) {
        case EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable:
          columns = new GoodsReceiptsList(
            EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable,
            customFields,
          ).fields;
          break;
      }
    } else if (baseModule == Helper.INVOICING_MODULE) {
      switch (type) {
        case EnumInvoicingFieldStructure.InvoicingTable:
          columns = new InvoicingList(EnumInvoicingFieldStructure.InvoicingTable, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.VENDORS_MODULE) {
      switch (type) {
        case EnumVendorsFieldStructure.VendorTable:
          columns = new VendorsListField(EnumVendorsFieldStructure.VendorTable, customFields)
            .fields;
          break;
        case EnumVendorsFieldStructure.VendorCreateForm:
          columns = new VendorsListField(EnumVendorsFieldStructure.VendorCreateForm, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.CARRIERS_MODULE) {
      switch (type) {
        case EnumCarriersFieldStructure.CarriersTable:
          columns = new CarriersListField(EnumCarriersFieldStructure.CarriersTable, customFields)
            .fields;
          break;
        case EnumCarriersFieldStructure.CarriersCreateForm:
          columns = new CarriersListField(
            EnumCarriersFieldStructure.CarriersCreateForm,
            customFields,
          ).fields;
          break;
      }
    } else if (baseModule == Helper.PARTNUMBERS_MODULE) {
      switch (type) {
        case EnumPartNumbersFieldStructure.PartNumberTable:
          columns = new PartNumbersListField(
            EnumPartNumbersFieldStructure.PartNumberTable,
            customFields,
          ).fields;
          break;
        case EnumPartNumbersFieldStructure.PartNumberCreateForm:
          columns = new PartNumbersListField(
            EnumPartNumbersFieldStructure.PartNumberCreateForm,
            customFields,
          ).fields;
          break;
      }
    } else if (baseModule == Helper.PORTFOLIO_MODULE) {
      switch (type) {
        case EnumPortfolioFieldStructure.PortfolioTable:
          columns = new PortfolioListField(EnumPortfolioFieldStructure.PortfolioTable, customFields)
            .fields;
          break;
        case EnumPortfolioFieldStructure.PortfolioCreateForm:
          columns = new PortfolioListField(
            EnumPortfolioFieldStructure.PortfolioCreateForm,
            customFields,
          ).fields;
          break;
        case EnumPortfolioFieldStructure.BulkEditForm:
          columns = new PortfolioListField(EnumPortfolioFieldStructure.BulkEditForm, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.SHIPMENTS_MODULE) {
      switch (type) {
        case EnumShipmentFieldStructure.ShipmentTable:
          columns = new ShipmentListField(EnumShipmentFieldStructure.ShipmentTable, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.STOCKS_MODULE) {
      switch (type) {
        case EnumStocksFieldStructure.StocksTable:
          columns = new StocksListField(EnumStocksFieldStructure.StocksTable, customFields).fields;
          break;
        case EnumStocksFieldStructure.StocksCreateForm:
          columns = new StocksListField(EnumStocksFieldStructure.StocksCreateForm, customFields)
            .fields;
          break;
      }
    } else if (baseModule == Helper.SLOTS_STORAGE_MODULE) {
      switch (type) {
        case EnumSlotsStorageFieldStructure.SlotsStorageTable:
          columns = new SlotsStorageField(
            EnumSlotsStorageFieldStructure.SlotsStorageTable,
            customFields,
          ).fields;
          break;
        case EnumSlotsStorageFieldStructure.SlotsStorageCreateForm:
          columns = new SlotsStorageField(
            EnumSlotsStorageFieldStructure.SlotsStorageCreateForm,
            customFields,
          ).fields;
          break;
      }
    } else if (baseModule == Helper.SLOTS_MODULE) {
      switch (type) {
        case EnumSlotsFieldStructure.SlotsTable:
          columns = new SlotsField(EnumSlotsFieldStructure.SlotsTable, customFields).fields;
          break;
      }
    }
    return columns;
  }

  public static formatColumns(
    settings: undefined | UserSettings,
    columns: Column[],
    columnAlreadySaved: boolean,
    type: number, //EnumCommitFieldStructure | EnumGoodsReceiptsFieldStructure | EnumInvoicingFieldStructure | EnumQuotationsFieldStructure | EnumVendorsFieldStructure | EnumCarriersFieldStructure | EnumPartNumbersFieldStructure | EnumPortfolioFieldStructure | EnumStocksFieldStructure,
    defaultFields: string[],
    baseModule?: string,
  ): { columnAlreadySaved: boolean; columns: Column[] } {
    columns = [...columns];
    columnAlreadySaved = !!(settings && settings?.key);

    if (columnAlreadySaved) {
      columns = Helper.formatColumnsWithUserSettings(
        settings as UserSettings,
        columns,
        columnAlreadySaved,
        type,
        defaultFields,
        baseModule,
      );
    } else {
      columns = Helper.formatColumnsWithoutUserSettings(
        settings as UserSettings,
        columns,
        columnAlreadySaved,
        type,
        defaultFields,
      );
    }
    return { columnAlreadySaved, columns };
  }

  public static formatColumnsWithUserSettings(
    settings: UserSettings,
    columns: Column[],
    columnAlreadySaved: boolean,
    type: number, //EnumCommitFieldStructure | EnumGoodsReceiptsFieldStructure | EnumInvoicingFieldStructure | EnumQuotationsFieldStructure | EnumVendorsFieldStructure | EnumCarriersFieldStructure | EnumPartNumbersFieldStructure | EnumPortfolioFieldStructure | EnumStocksFieldStructure,
    defaultFields: string[],
    baseModule?: string,
  ): Column[] {
    settings = plainToInstance(UserSettingsCls, settings);
    let data = plainToInstance(
      FieldVisibilitySetting,
      JSON.parse(settings.data) as FieldVisibilitySetting[],
    );
    let selectedList = [];
    // data Mapping For Model
    const enumTypesTables = [
      EnumCommitFieldStructure.CommitTable,
      EnumGoodsReceiptsFieldStructure.GoodsReceiptsTable,
      EnumInvoicingFieldStructure.InvoicingTable,
      EnumVendorsFieldStructure.VendorTable,
      EnumCarriersFieldStructure.CarriersTable,
      EnumPartNumbersFieldStructure.PartNumberTable,
      EnumPortfolioFieldStructure.PortfolioTable,
      EnumStocksFieldStructure.StocksTable,
      EnumSlotsFieldStructure.SlotsTable,
    ];
    const enumTypesForms = [
      EnumCommitFieldStructure.CommitForm,
      EnumVendorsFieldStructure.VendorCreateForm,
      EnumCarriersFieldStructure.CarriersCreateForm,
      EnumPortfolioFieldStructure.PortfolioCreateForm,
      EnumStocksFieldStructure.StocksCreateForm,
    ];

    if (data) {
      if (enumTypesTables.includes(type) || enumTypesForms.includes(type)) {
        if (baseModule == Helper.PORTFOLIO_MODULE) {
          // Unfiltered catalogue, used to recover a caption and a render type
          // for stored columns that the rights-filtered `columns` left out.
          // It has to be the unfiltered one: the filtered list is exactly what
          // the lookup below already missed.
          const catalogue = new PortfolioListField(EnumPortfolioFieldStructure.PortfolioTable)
            .fields;

          for (let i = 0; i < data.length; i++) {
            const dataItem = data[i];

            // Check if this field exists in columns
            const col = columns.find((c): boolean => c.field === dataItem.field);

            if (col) {
              // If field exists, update visibility and index
              col.visible = dataItem.visible;
              col.index = dataItem.index;
            } else {
              // Pole z user settingů včetně addition fieldů.
              //
              // Caption and render type are read back rather than invented,
              // otherwise `flags` renders as raw text and every column is
              // labelled with its own field name.
              //
              // Catalogue first: it is the single source of truth for the
              // fields it knows, and a caption stored earlier would be the
              // stale one after a rename. The stored record is next, for the
              // rare save that carries a caption at all — `saveFieldsVisibility`
              // persists only field, index and visible. A genuine addition
              // field is in neither, and its own name is the right label.
              const known = catalogue.find(
                (entry: MappedField): boolean =>
                  entry.field.toLowerCase() === dataItem.field.toLowerCase(),
              );

              columns.push({
                field: dataItem.field,
                headerText: known?.headerText || dataItem.headerText || dataItem.field,
                index: dataItem.index,
                isRequired: dataItem.isRequired,
                type: known?.type || dataItem.type || 'string',
                visible: dataItem.visible,
              });
            }
          }
        } else {
          for (let i = 0; i < columns.length; i++) {
            const temp = data.find((d): boolean => d.field == columns[i].field);
            if (temp) {
              columns[i].visible = temp.visible;
              columns[i].index = temp.index;
            }
          }
        }
        columns = Helper.setColumnOrder(columns);
      } else {
        if (data.length > 0) {
          for (let i = 0; i < columns.length; i++) {
            const temp = data.find(
              (d): boolean =>
                d.field.toLowerCase() ==
                (columns[i]?.field && (columns[i]?.field as string).toLowerCase()),
            );
            if (temp && temp.visible) {
              selectedList.push(columns[i]);
            }
          }
        } else {
          selectedList = [...columns];
        }

        let bulkRemoveList: string[] = [];

        switch (type) {
          case EnumCommitFieldStructure.BulkEditForm:
            bulkRemoveList = [
              'partnumber',
              'purchaseorderline',
              'purchaseordernumber',
              'vendorcode',
              'responsibleemail',
            ];
            break;
          case EnumPortfolioFieldStructure.BulkEditForm:
            bulkRemoveList = [
              'partnumber',
              'vendorcode',
              'firstname',
              'lastname',
              'validfrom',
              'validto',
            ];
            break;
          default:
            bulkRemoveList = [];
            break;
        }

        //remove fields from bulkEdit
        if (bulkRemoveList.length > 0) {
          selectedList = selectedList.filter(
            (val): boolean => !bulkRemoveList.includes(val?.field?.toLowerCase() as string),
          );
        }

        //remove duplicates
        selectedList = this.removeDuplicateObjects(selectedList, 'headerText');
        //sorting Fields
        columns = Helper.setColumnOrderByAlpha(selectedList);
      }
    } else {
      columns = Helper.setDefaultColumns(defaultFields, columns, type, baseModule);
    }
    return columns;
  }

  public static formatColumnsWithoutUserSettings(
    settings: UserSettings,
    columns: Column[],
    columnAlreadySaved: boolean,
    type: number, //EnumCommitFieldStructure | EnumGoodsReceiptsFieldStructure | EnumInvoicingFieldStructure | EnumQuotationsFieldStructure | EnumVendorsFieldStructure | EnumCarriersFieldStructure | EnumPartNumbersFieldStructure | EnumPortfolioFieldStructure | EnumStocksFieldStructure,
    defaultFields: string[],
  ): Column[] {
    const actionsColumnId = 'actions';

    // First, filter columns based on defaultFields case-insensitively
    const filteredColumns = columns
      .map((col, index): (Column & { index: number }) | null => {
        if (
          defaultFields.some(
            (field): boolean =>
              field.toLowerCase() === (col.field as string).toLowerCase() || col.mandatory == true,
          )
        ) {
          return { ...col, index };
        }
        return null;
      })
      .filter((col): boolean => col !== null); // Remove any nulls from non-matching fields

    // Find the Actions column in the original columns array
    const actionsColumn = columns.find(
      (col): boolean => (col.field as string).toLowerCase() === actionsColumnId,
    );

    // If the Actions column exists, add it to the beginning of filteredColumns
    if (actionsColumn) {
      filteredColumns.unshift({ ...actionsColumn, index: columns.indexOf(actionsColumn) });
    }
    columns = filteredColumns as Column[];
    return columns;
  }

  public static setColumnOrder(columns: Column[]): Column[] {
    columns = [...columns];
    return columns.sort((a, b): number => (a.index! < b.index! ? -1 : a.index! > b.index! ? 1 : 0));
  }

  private static setColumnOrderByAlpha(columns: Column[]): Column[] {
    return [...columns].sort((a, b): number => {
      if (a.name && b.name) {
        return (a.name as string).toLowerCase().localeCompare((b.name as string).toLowerCase());
      }
      return 0;
    });
  }

  private static removeDuplicateObjects<T extends Record<string, unknown>>(
    array: T[],
    property: string,
  ): T[] {
    return [...new Map(array.map((item): [unknown, T] => [item[property], item])).values()];
  }

  public static getVisibleColumns(columns: Column[], data?: ColumnData): string[] {
    let visibleColumns = [...columns];

    if (data?.baseModule === Helper.COMMIT_MODULE) {
      visibleColumns = Helper.createCopy(visibleColumns).sort((a, b): number => {
        const fieldA = (a.field as string).toLowerCase();
        const fieldB = (b.field as string).toLowerCase();
        return data.defaultFields.indexOf(fieldA) - data.defaultFields.indexOf(fieldB);
      });
    }

    return visibleColumns
      .filter((column): boolean => column.visible === true)
      .map((column): string => column.field as string);
  }

  public static formatFontsize(fontSize: string): number {
    let fontSizeInt;

    switch (fontSize) {
      case 'small':
        fontSizeInt = 9;
        break;

      case 'medium':
        fontSizeInt = 10;
        break;

      case 'large':
        fontSizeInt = 11;
        break;

      default:
        fontSizeInt = 10;
        break;
    }
    return fontSizeInt;
  }

  public static userRoles: Record<string, Array<{ name: string; role: string }>> = {
    Finance: [
      { name: 'Finance Analyst', role: 'Orion_Finance_Analyst' },
      { name: 'Finance Accountant', role: 'Orion_Finance_Accountant' },
    ],
    Logistic: [
      { name: 'Logistic Analyst', role: 'Orion_Logistics_Analyst' },
      { name: 'Logistic', role: 'Orion_Logistics_Logistic' },
      { name: 'Logistic Carrier', role: 'Orion_Logistics_Carrier' },
      { name: 'Logistic Warehouse', role: 'Orion_Logistics_Warehouse' },
      { name: 'Logistic Declarant', role: 'Orion_Logistics_Declarant' },
      { name: 'Logistic Readonly', role: 'Orion_Logistics_Readonly' },
      { name: 'Warehouse Analyst', role: 'Orion_Warehouse_Analyst' },
      { name: 'Logistic Slot', role: 'Orion_Logistics_Slot' },
    ],
    SCM: [
      { name: 'Analyst', role: 'Orion_SCM_Analyst' },
      { name: 'Buyer', role: 'Orion_SCM_Buyer' },
      { name: 'MasterBuyer', role: 'Orion_SCM_Master_Buyer' },
      { name: 'Supplier Commit', role: 'Orion_SCM_Supplier_Commit' },
      { name: 'Supplier BS', role: 'Orion_SCM_Supplier_BS' },
      { name: 'Supplier ROP', role: 'Orion_SCM_Supplier_ROP' },
      { name: 'Customer', role: 'Orion_SCM_Customer' },
      { name: 'Engineering', role: 'Orion_SCM_Engineering' },
      { name: 'Customer Commit', role: 'Orion_SCM_Customer_Commit' },
      { name: 'Sourcing', role: 'Orion_SCM_Sourcing' },
    ],
  };

  public static getUserRightByRole(role: string): UserRights {
    let userRights!: UserRights;
    switch (role) {
      case 'Orion_SCM_Analyst':
        const supplyVisibilityAnalystRights: SupplyVisibilityRights = {
          cancelCommits: true,
          canConfigureInfoFields: true,
          commentsEdit: true,
          commentsView: true,
          commitsLayout: true,
          deleteCommits: true,
          dummyCommits: true,
          forecastView: true,
          graphsEdit: true,
          graphsView: true,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          masterDataView: true,
          mergeCommits: true,
          notesEdit: true,
          notesView: true,
          remergeCommits: true,
          reviewFlags: true,
          reviewFlagsEdit: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForCustomer: true,
          reviewFlagsForCustomerEdit: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForSupplierEdit: true,
          selectCommitFields: true,
          splitCommits: true,
        };

        /*Dates types visibility*/
        const dateTypeAnalystRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
          'createDate',
        ];
        /*Status visibility*/
        const statusAnalystRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleAnalystRights: CommitsModuleRights = {
          cancelCommits: true,
          createCommits: true,
          defaultFieldsSetup: true,
          deleteCommits: true,
          deletePLO: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          lockCommit: true,
          massChanges: true,
          mergeCommits: true,
          remergeCommits: true,
          searchCommits: true,
          selectFields: true,
          splitCommits: true,
        };

        const dashboardsAnalystRights: DashboardsRights = {
          sharedDashboards: true,
        };

        const vendorListAnalystRights: VendorListRights = {
          buyersManage: true,
          buyersView: true,
          flagEdit: true,
          notificationsSetupEdit: true,
          notificationsSetupView: true,
          partNumbersDelete: true,
          partNumbersEdit: true,
          partNumbersView: true,
          vendorCreate: true,
          vendorDelete: true,
          vendorEdit: true,
          vendorView: true,
        };

        const configurationAnalystRights: ConfigurationRights = {
          materialManagementViewsView: true,
          qapSettings: true,
          reviewVisibility: true,
          searchConfiguration: true,
          vendorCodesChange: true,
          vendorCodesView: true,
          virtualGroupsChange: true,
          virtualGroupsView: true,
        };

        const carriersModuleAnalystRights: CarriersModuleRights = {
          carriersCreate: true,
          carriersDelete: true,
          carriersEdit: true,
          carriersView: true,
          isExpandable: true,
        };

        const shipmentsModuleAnalystRights: ShipmentsModuleRights = {
          //shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          //shipmentsDocumentsUpload: true,
          //shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsView: true,
          //shipmentInternalDocumentsView: true,
          //shipmentInternalDocumentsUpload: true,
          //shipmentInternalDocumentsDownload: true,
          //shipmentInternalDocumentsDelete: true,
          shipmentsFlagsView: true,
          shipmentsView: true,
          //shipmentsFlagsEdit: true,
        };
        const stocksModuleAnalystRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
        };

        const finantialModuleAnalystRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: true,
          selectFields: true,
        };
        const dateTypeAnalystRights: DateTypeRights = {
          actualETADate: true,
          createDate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Menu visibility*/
        const menuAccessAnalystRightList: string[] = [
          'Commits',
          'PN Groups',
          'Vendor Codes Rights',
          'My Portfolio',
          'Vendors',
          'Carriers',
          'Stocks',
          'Contacts',
          'PartNumbers',
          'QAP',
          'RFQ',
          'Portfolio',
          'NotesAndComments',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Shipments',
          'Audit Log',
          'SCM Default Fields',
          'Financial Module Default Fields',
          'Logistic Default Fields',
        ];

        /*Widget visibility*/
        const widgetAccessAnalystRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'pnGroupsLink',
          'vendorcodeRightsLink',
          'fileUpload',
          'mypnlist',
          'ploSync',
          'configurationLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
          'portfolioLink',
          'slotList',
        ];

        userRights = {
          carriersModuleRights: carriersModuleAnalystRights,
          commitsModuleRights: commitsModuleAnalystRights,
          configurationRights: configurationAnalystRights,
          dashboardRights: dashboardsAnalystRights,
          dateTypeRightList: dateTypeAnalystRightList,
          dateTypeRights: dateTypeAnalystRights,
          finantialModuleRights: finantialModuleAnalystRights,
          menuAccessRightList: menuAccessAnalystRightList,
          shipmentsModuleRights: shipmentsModuleAnalystRights,
          statusRightList: statusAnalystRightList,
          stocksModuleRights: stocksModuleAnalystRights,
          supplyVisibilityRights: supplyVisibilityAnalystRights,
          vendorListRights: vendorListAnalystRights,
          widgetAccessRightList: widgetAccessAnalystRightList,
        };

        break;

      case 'Orion_Administrator':
        const supplyVisibilityAdministratorRights: SupplyVisibilityRights = {
          cancelCommits: true,
          canConfigureInfoFields: true,
          commentsEdit: true,
          commentsView: true,
          commitsLayout: true,
          deleteCommits: true,
          dummyCommits: true,
          forecastView: true,
          graphsEdit: true,
          graphsView: true,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          masterDataView: true,
          mergeCommits: true,
          notesEdit: true,
          notesView: true,
          remergeCommits: true,
          reviewFlags: true,
          reviewFlagsEdit: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForCustomer: true,
          reviewFlagsForCustomerEdit: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForSupplierEdit: true,
          selectCommitFields: true,
          splitCommits: true,
        };

        /*Dates types visibility*/
        const dateTypeAdministratorRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
          'createDate',
        ];
        /*Status visibility*/
        const statusAdministratorRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleAdministratorRights: CommitsModuleRights = {
          cancelCommits: true,
          createCommits: true,
          defaultFieldsSetup: true,
          deleteCommits: true,
          deletePLO: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          lockCommit: true,
          massChanges: true,
          mergeCommits: true,
          remergeCommits: true,
          searchCommits: true,
          selectFields: true,
          splitCommits: true,
        };

        const dashboardsAdministratorRights: DashboardsRights = {
          sharedDashboards: true,
        };

        const vendorListAdministratorRights: VendorListRights = {
          buyersManage: true,
          buyersView: true,
          flagEdit: true,
          notificationsSetupEdit: true,
          notificationsSetupView: true,
          partNumbersDelete: true,
          partNumbersEdit: true,
          partNumbersView: true,
          vendorCreate: true,
          vendorDelete: true,
          vendorEdit: true,
          vendorView: true,
        };

        const configurationAdministratorRights: ConfigurationRights = {
          materialManagementViewsView: true,
          qapSettings: true,
          reviewVisibility: true,
          searchConfiguration: true,
          vendorCodesChange: true,
          vendorCodesView: true,
          virtualGroupsChange: true,
          virtualGroupsView: true,
        };

        const finantialModuleAdministratorRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: true,
          selectFields: true,
        };
        const dateTypeAdministratorRights: DateTypeRights = {
          actualETADate: true,
          createDate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        const carriersModuleAdministratorRights: CarriersModuleRights = {
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersCreate: true,
          carriersDelete: true,
          carriersEdit: true,
          carriersNotificationsDelete: true,
          carriersNotificationsEdit: true,
          carriersTransportsCreate: true,
          carriersTransportsDelete: true,
          carriersView: true,
          isExpandable: true,
        };

        const stocksModuleAdministratorRights: StocksModuleRights = {
          isExpandable: true,
          stocksCreate: true,
          stocksDelete: true,
          stocksEdit: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true,
          stocksView: true,
        };

        const shipmentsModuleAdministratorRights: ShipmentsModuleRights = {
          setInstructionTemplate: true,
          setTemplateEmailBody: true,
          setTemplateEmailSubject: true,
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,
          shipmentsInstructionsView: true,
          shipmentsMrnUpload: true,
          shipmentsView: true,
        };

        const slotsStorageModuleAdministratorRights: SlotsStorageModuleRights = {
          isExpandable: true,
          slotsStorageCreate: true,
          slotsStorageDelete: true,
          slotsStorageEdit: true,
          slotsStorageView: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursDelete: true,
          slotStorageWorkingHoursEdit: true,
        };

        const slotsModuleAdministratorRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsRequest: true,
          slotsUpload: true,
          slotsView: true,
        };

        /*Menu visibility*/
        const menuAccessAdministratorRightList: string[] = [
          'Commits',
          'PN Groups',
          'Vendor Codes Rights',
          'My Portfolio',
          'Vendors',
          'Carriers',
          'Stocks',
          'Contacts',
          'PartNumbers',
          'QAP',
          'RFQ',
          'Portfolio',
          'NotesAndComments',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Shipments',
          'Shipments Update',
          'Audit Log',
          'SCM Default Fields',
          'Financial Module Default Fields',
          'Logistic Default Fields',
        ];

        /*Widget visibility*/
        const widgetAccessAdministratorRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'pnGroupsLink',
          'vendorcodeRightsLink',
          'fileUpload',
          'mypnlist',
          'ploSync',
          'configurationLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'reviewSupplyVisibility',
          'simplePNList',
          'invoiceList',
          'aiLink',
          'portfolioLink',
          'shipmentsList',
          'slotList',
        ];

        userRights = {
          carriersModuleRights: carriersModuleAdministratorRights,
          commitsModuleRights: commitsModuleAdministratorRights,
          configurationRights: configurationAdministratorRights,
          dashboardRights: dashboardsAdministratorRights,
          dateTypeRightList: dateTypeAdministratorRightList,
          dateTypeRights: dateTypeAdministratorRights,
          finantialModuleRights: finantialModuleAdministratorRights,
          menuAccessRightList: menuAccessAdministratorRightList,
          shipmentsModuleRights: shipmentsModuleAdministratorRights,
          slotsModuleRights: slotsModuleAdministratorRights,
          slotsStorageModuleRights: slotsStorageModuleAdministratorRights,
          statusRightList: statusAdministratorRightList,
          stocksModuleRights: stocksModuleAdministratorRights,
          supplyVisibilityRights: supplyVisibilityAdministratorRights,
          vendorListRights: vendorListAdministratorRights,
          widgetAccessRightList: widgetAccessAdministratorRightList,
        };

        break;

      case 'Orion_SCM_Buyer':
        const supplyVisibilityBuyerRights: SupplyVisibilityRights = {
          commentsEdit: true,
          commentsView: true,
          commitsLayout: true,
          //cancelCommits: false,
          deleteCommits: true,
          dummyCommits: true,
          forecastView: true,
          graphsView: true,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          masterDataView: true,
          mergeCommits: true,
          notesEdit: true,
          notesView: true,
          remergeCommits: true,
          reviewFlags: true,
          reviewFlagsEdit: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForCustomer: true,
          reviewFlagsForSupplier: true,
          selectCommitFields: true,
          splitCommits: true,
        };

        /*Dates types visibility*/
        const dateTypeBuyerRightList: string[] = [
          'sapDeliveryDate',
          'etdDate',
          'receiveDate', //grdate
          'triggerDate',
          'plannedOrderStatus',
          'createDate',
        ];

        /*Status visibility*/
        const statusBuyerRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleBuyerRights: CommitsModuleRights = {
          createCommits: true,
          //cancelCommits: false,
          deleteCommits: true,
          deletePLO: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          lockCommit: true,
          massChanges: true,
          mergeCommits: true,
          remergeCommits: true,
          searchCommits: true,
          selectFields: true,
          splitCommits: true,
        };

        const dashboardsBuyerRights: DashboardsRights = {
          //sharedDashboards : false,
        };

        const vendorListBuyerRights: VendorListRights = {
          buyersManage: true,
          buyersView: true,
          //notificationsSetupEdit : false,
          flagEdit: true,
          notificationsSetupView: true,
          partNumbersDelete: true,
          partNumbersEdit: true,
          partNumbersView: true,
          vendorCreate: true,
          vendorDelete: true,
          vendorEdit: true,
          vendorView: true,
        };

        const configurationBuyerRights: ConfigurationRights = {
          //vendorCodesChange: false,
          //materialManagementViewsView : false,
          reviewVisibilityForBuyer: true,
          searchConfiguration: true,
          vendorCodesView: true,
          virtualGroupsChange: true,
          virtualGroupsView: true,
        };

        const finantialModuleBuyerRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: true,
          selectFields: true,
        };

        const shipmentsModuleBuyerRights: ShipmentsModuleRights = {
          //shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          //shipmentsDocumentsUpload: true,
          //shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsView: true,
          //shipmentInternalDocumentsView: true,
          //shipmentInternalDocumentsUpload: true,
          //shipmentInternalDocumentsDownload: true,
          //shipmentInternalDocumentsDelete: true,
          shipmentsFlagsView: true,
          shipmentsView: true,
          //shipmentsFlagsEdit: true,
        };

        const stocksModuleBuyerRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
        };

        const dateTypeBuyerRights: DateTypeRights = {
          createDate: true,
          etdDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          sapDeliveryDate: true /*Final ETA*/,
          triggerDate: true,
        };

        /*Menu visibility*/
        const menuAccessBuyerRightList: string[] = [
          'Commits',
          'PN Groups',
          'Vendor Codes Rights',
          'My Portfolio',
          'Vendors',
          'Shipments',
          'Stocks',
          'Contacts',
          'PartNumbers',
          'QAP',
          'RFQ',
          'Portfolio',
          'NotesAndComments',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Audit Log',
        ];

        /*Widget visibility*/
        const widgetAccessBuyerRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'pnGroupsLink',
          'vendorcodeRightsLink',
          'fileUpload',
          'mypnlist',
          'ploSync',
          'configurationLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
          'portfolioLink',
        ];

        userRights = {
          commitsModuleRights: commitsModuleBuyerRights,
          configurationRights: configurationBuyerRights,
          dashboardRights: dashboardsBuyerRights,
          dateTypeRightList: dateTypeBuyerRightList,
          dateTypeRights: dateTypeBuyerRights,
          finantialModuleRights: finantialModuleBuyerRights,
          menuAccessRightList: menuAccessBuyerRightList,
          shipmentsModuleRights: shipmentsModuleBuyerRights,
          statusRightList: statusBuyerRightList,
          stocksModuleRights: stocksModuleBuyerRights,
          supplyVisibilityRights: supplyVisibilityBuyerRights,
          vendorListRights: vendorListBuyerRights,
          widgetAccessRightList: widgetAccessBuyerRightList,
        };

        break;
      case 'Orion_SCM_Supplier_BS':
        const supplyVisibilitySupplierBSRights: SupplyVisibilityRights = {
          commentsEdit: true,
          commentsView: true,
          //cancelCommits: false,
          deleteCommits: true,
          //commitsLayout: false,
          dummyCommits: true,
          forecastView: true,
          //graphsView: false,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          mergeCommits: true,
          //masterDataView: false,
          //notesEdit: false,
          //notesView: false,
          //reviewFlags: false,
          reviewFlags: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForSupplierEdit: true,
          splitCommits: true,
          //selectCommitFields: true,
        };

        /*Dates types visibility*/
        const dateTypeSupplierBSRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusSupplierBSRightList: string[] = ['Delivered', 'Waiting'];

        const commitsModuleSupplierBSRights: CommitsModuleRights = {
          createCommits: true,
          //cancelCommits: false,
          deleteCommits: true,
          documentsCreate: true,
          documentsDelete: true,
          //deletePLO: false,
          //remergeCommits: false,
          //selectFields: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          //layoutChanges: false,
          massChanges: true,
          mergeCommits: true,
          searchCommits: true,
          splitCommits: true,
        };

        const dashboardsSupplierBSRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListSupplierBSRights: VendorListRights = {
          /*notificationsSetupView : false,
          notificationsSetupEdit : false,
          flagEdit : false,
          vendorCreate: false,
          vendorView: false,
          vendorEdit: false,
          vendorDelete: false,
          partNumbersView: false,
          partNumbersEdit: false,
          partNumbersDelete: false,
          buyersView: false,
          buyersManage: false,*/
        };

        const configurationSupplierBSRights: ConfigurationRights = {
          reviewVisibilityForSupplier: true,
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleSupplierBSRights: FinancialModuleRights = {};

        const dateTypeSupplierBSRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Menu visibility*/
        const menuAccessSupplierBSRightList: string[] = ['Commits', 'QAP'];

        /*Widget visibility*/
        const widgetAccessSupplierBSRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'mypnlist',
          'fileUpload',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleSupplierBSRights,
          configurationRights: configurationSupplierBSRights,
          dashboardRights: dashboardsSupplierBSRights,
          dateTypeRightList: dateTypeSupplierBSRightList,
          dateTypeRights: dateTypeSupplierBSRights,
          finantialModuleRights: finantialModuleSupplierBSRights,
          menuAccessRightList: menuAccessSupplierBSRightList,
          statusRightList: statusSupplierBSRightList,
          supplyVisibilityRights: supplyVisibilitySupplierBSRights,
          vendorListRights: vendorListSupplierBSRights,
          widgetAccessRightList: widgetAccessSupplierBSRightList,
        };

        break;

      case 'Orion_SCM_Supplier_Commit':
        const supplyVisibilitySupplierRights: SupplyVisibilityRights = {
          commentsEdit: true,
          commentsView: true,
          //cancelCommits: false,
          deleteCommits: true,
          //commitsLayout: false,
          dummyCommits: true,
          forecastView: true,
          //graphsView: false,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          mergeCommits: true,
          //masterDataView: false,
          //notesEdit: false,
          //notesView: false,
          //reviewFlags: false,
          reviewFlags: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForSupplierEdit: true,
          splitCommits: true,
          //selectCommitFields: true,
        };

        /*Dates types visibility*/
        const dateTypeSupplierRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusSupplierRightList: string[] = ['Delivered', 'Waiting'];

        const commitsModuleSupplierRights: CommitsModuleRights = {
          createCommits: true,
          //cancelCommits: false,
          deleteCommits: true,
          documentsCreate: true,
          documentsDelete: true,
          //deletePLO: false,
          //remergeCommits: false,
          //selectFields: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          //layoutChanges: false,
          massChanges: true,
          mergeCommits: true,
          searchCommits: true,
          splitCommits: true,
        };

        const dashboardsSupplierRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListSupplierRights: VendorListRights = {
          /*notificationsSetupView : false,
          notificationsSetupEdit : false,
          flagEdit : false,
          vendorCreate: false,
          vendorView: false,
          vendorEdit: false,
          vendorDelete: false,
          partNumbersView: false,
          partNumbersEdit: false,
          partNumbersDelete: false,
          buyersView: false,
          buyersManage: false,*/
        };

        const configurationSupplierRights: ConfigurationRights = {
          reviewVisibilityForSupplier: true,
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleSupplierRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          /*quotations: false*/
        };

        const dateTypeSupplierRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Menu visibility*/
        const menuAccessSupplierRightList: string[] = [
          'Commits',
          'QAP',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
        ];

        /*Widget visibility*/
        const widgetAccessSupplierRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'mypnlist',
          'fileUpload',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleSupplierRights,
          configurationRights: configurationSupplierRights,
          dashboardRights: dashboardsSupplierRights,
          dateTypeRightList: dateTypeSupplierRightList,
          dateTypeRights: dateTypeSupplierRights,
          finantialModuleRights: finantialModuleSupplierRights,
          menuAccessRightList: menuAccessSupplierRightList,
          statusRightList: statusSupplierRightList,
          supplyVisibilityRights: supplyVisibilitySupplierRights,
          vendorListRights: vendorListSupplierRights,
          widgetAccessRightList: widgetAccessSupplierRightList,
        };

        break;

      case 'Orion_SCM_Supplier_No_Commit':
      case 'Orion_SCM_Supplier_ROP':
        const supplyVisibilitySupplierROPRights: SupplyVisibilityRights = {
          commentsEdit: true,
          commentsView: true,
          //commitsLayout: false,
          dummyCommits: true,
          forecastView: true,
          managePredefinedComments: true,
          mergeCommits: true,
          //graphsView: false,
          //manageCommits: false,
          //manageDummyCommits: false,
          //masterDataView: false,
          //notesEdit: false,
          //notesView: false,
          reviewFlags: true,
          //cancelCommits: false,
          //deleteCommits: false,
          splitCommits: true,
          //manageSimulationSets: true,
          //selectCommitFields: false,
        };

        /*Dates types visibility*/
        const dateTypeSupplierROPRightList: string[] = ['receiveDate', 'plannedOrderStatus'];

        /*Status visibility*/
        const statusSupplierROPRightList: string[] = ['Delivered', 'New', 'Waiting'];

        const commitsModuleSupplierROPRights: CommitsModuleRights = {
          documentsCreate: true,
          documentsDelete: true,
          //remergeCommits: false,
          //selectFields: false,
          documentsView: true,
          downloadExcel: true,
          //createCommits: false,
          editCommits: true,
          //layoutChanges: false,
          massChanges: true,
          mergeCommits: true,
          searchCommits: true,
          //cancelCommits: false,
          //deleteCommits: false,
          //deletePLO: false,
          splitCommits: true,
        };

        const dashboardsSupplierROPRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListSupplierROPRights: VendorListRights = {
          /*notificationsSetupView : false,
          notificationsSetupEdit : false,
          flagEdit:false,
          vendorCreate: false,
          vendorView: false,
          vendorEdit: false,
          vendorDelete: false,
          partNumbersView: false,
          partNumbersEdit: false,
          partNumbersDelete: false,
          buyersView: false,
          buyersManage: false,*/
        };

        const configurationSupplierROPRights: ConfigurationRights = {
          /*searchConfiguration: false
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleSupplierROPRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: false,
        };

        const dateTypeSupplierROPRights: DateTypeRights = {
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
        };

        /*Menu visibility*/
        const menuAccessSupplierROPRightList: string[] = [
          'Commits',
          'QAP',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
        ];

        /*Widget visibility*/
        const widgetAccessSupplierROPRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'mypnlist',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleSupplierROPRights,
          configurationRights: configurationSupplierROPRights,
          dashboardRights: dashboardsSupplierROPRights,
          dateTypeRightList: dateTypeSupplierROPRightList,
          dateTypeRights: dateTypeSupplierROPRights,
          finantialModuleRights: finantialModuleSupplierROPRights,
          menuAccessRightList: menuAccessSupplierROPRightList,
          statusRightList: statusSupplierROPRightList,
          supplyVisibilityRights: supplyVisibilitySupplierROPRights,
          vendorListRights: vendorListSupplierROPRights,
          widgetAccessRightList: widgetAccessSupplierROPRightList,
        };

        break;
      case 'Orion_SCM_Customer':
        const supplyVisibilityCustomerRights: SupplyVisibilityRights = {
          //commentsEdit: false,
          commentsView: true,
          commitsLayout: true,
          disableCommitManagement: true,
          //dummyCommits: false,
          forecastView: true,
          graphsView: true,
          manageCommits: true,
          manageDummyCommits: true,
          /*cancelCommits: false,
          deleteCommits: false,*/
          manageSimulationSets: true,
          masterDataView: true,
          /*notesEdit: false,
          notesView: false,*/
          reviewFlags: true,
          reviewFlagsForCustomer: true,
          reviewFlagsForCustomerEdit: true,
          selectCommitFields: true,
        };

        /*Dates types visibility*/
        const dateTypeCustomerRightList: string[] = ['sapDeliveryDate'];

        const commitsModuleCustomerRights: CommitsModuleRights = {
          documentsView: true,
          //createCommits: false,
          //editCommits: false,
          downloadExcel: true,
          layoutChanges: true,
          //massChanges: false,
          searchCommits: true,
          //cancelCommits: false,
          //deleteCommits: false,
          //deletePLO: false,
          //remergeCommits: false,
          selectFields: true,
        };

        const dashboardsCustomerRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListCustomerRights: VendorListRights = {
          /*notificationsSetupView : false,
          notificationsSetupEdit : false,
          flagEdit:false,
          vendorCreate: false,
          vendorView: false,
          vendorEdit: false,
          vendorDelete: false,
          partNumbersView: false,
          partNumbersEdit: false,
          partNumbersDelete: false,
          buyersView: false,
          buyersManage: false,*/
        };

        const configurationCustomerRights: ConfigurationRights = {
          reviewVisibilityForCustomer: true,
          //searchConfiguration: true
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleCustomerRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          selectFields: true,
        };

        const dateTypeCustomerRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Status visibility*/
        const statusCustomerRightList: string[] = ['Delivered', 'Waiting'];

        /*Menu visibility*/
        const menuAccessCustomerRightList: string[] = [
          'Commits',
          'My Portfolio' /*'Contacts',*/,
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
        ];

        /*Widget visibility*/
        const widgetAccessCustomerRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'mypnlist',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleCustomerRights,
          configurationRights: configurationCustomerRights,
          dashboardRights: dashboardsCustomerRights,
          dateTypeRightList: dateTypeCustomerRightList,
          dateTypeRights: dateTypeCustomerRights,
          finantialModuleRights: finantialModuleCustomerRights,
          menuAccessRightList: menuAccessCustomerRightList,
          statusRightList: statusCustomerRightList,
          supplyVisibilityRights: supplyVisibilityCustomerRights,
          vendorListRights: vendorListCustomerRights,
          widgetAccessRightList: widgetAccessCustomerRightList,
        };

        break;

      case 'Orion_SCM_ReadOnly':
      case 'Orion_SCM_Engineering':
        const supplyVisibilityEngineeringRights: SupplyVisibilityRights = {
          commentsView: true,
          commitsLayout: true,
          forecastView: true,
          graphsView: true,
          manageSimulationSets: true,
          masterDataView: true,
          reviewFlags: true,
          selectCommitFields: true,
        };

        /*Dates types visibility*/
        const dateTypeEngineeringRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusEngineeringRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleEngineeringRights: CommitsModuleRights = {
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
        };

        const dashboardsEngineeringRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListEngineeringRights: VendorListRights = {
          notificationsSetupView: true,
          partNumbersView: true,
          vendorView: true,
        };

        const configurationEngineeringRights: ConfigurationRights = {
          //searchConfiguration: true
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleEngineeringRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          selectFields: true,
        };

        const dateTypeEngineeringRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Menu visibility*/
        const menuAccessEngineeringRightList: string[] = [
          'Commits',
          'My Portfolio' /*'Vendors',  'Carriers',*/,
          /*'Contacts', 'PartNumbers', */ 'QAP',
          'RFQ',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Audit Log',
          'Portfolio',
        ];

        /*Widget visibility*/
        const widgetAccessEngineeringRightList: string[] = [
          'statusMonitor',
          'mypnlist',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleEngineeringRights,
          configurationRights: configurationEngineeringRights,
          dashboardRights: dashboardsEngineeringRights,
          dateTypeRightList: dateTypeEngineeringRightList,
          dateTypeRights: dateTypeEngineeringRights,
          finantialModuleRights: finantialModuleEngineeringRights,
          menuAccessRightList: menuAccessEngineeringRightList,
          statusRightList: statusEngineeringRightList,
          supplyVisibilityRights: supplyVisibilityEngineeringRights,
          vendorListRights: vendorListEngineeringRights,
          widgetAccessRightList: widgetAccessEngineeringRightList,
        };

        break;

      case 'Orion_SCM_Customer_Commit':
        const supplyVisibilityCustomerCommitRights: SupplyVisibilityRights = {
          disableCommitManagement: false,
          manageCommits: true,
          massChanges: true,
          mergeCommits: true,
          remergeCommits: true,
          reviewFlags: true,
          splitCommits: true,
        };

        /*Dates types visibility*/
        const dateTypeCustomerCommitRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusCustomerCommitRightList: string[] = ['Delivered', 'Waiting'];

        const commitsModuleCustomerCommitRights: CommitsModuleRights = {
          createCommits: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          editCommits: true,
          massChanges: true,
          mergeCommits: true,
          splitCommits: true,
        };

        const dashboardsCustomerCommitRights: DashboardsRights = {
          //dashboardEdit : false,
          //dashboardView : true,
          //sharedDashboards : false,
        };

        const vendorListCustomerCommitRights: VendorListRights = {
          /*notificationsSetupView : false,
          notificationsSetupEdit : false,
          flagEdit:false,
          vendorCreate: false,
          vendorView: false,
          vendorEdit: false,
          vendorDelete: false,
          partNumbersView: false,
          partNumbersEdit: false,
          partNumbersDelete: false,
          buyersView: false,
          buyersManage: false,*/
        };

        const configurationCustomerCommitRights: ConfigurationRights = {
          //searchConfiguration: true
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleCustomerCommitRights: FinancialModuleRights = {
          //invoicesView: true,
          //invoicesExport: true,
          //invoiceReconcilation: true,
          //goodReceipts : true
        };

        const dateTypeCustomerCommitRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Menu visibility*/
        const menuAccessCustomerCommitRightList: string[] = [
          'Commits',
          'My Portfolio',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
        ];

        /*Widget visibility*/
        const widgetAccessCustomerCommitRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'mypnlist',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleCustomerCommitRights,
          configurationRights: configurationCustomerCommitRights,
          dashboardRights: dashboardsCustomerCommitRights,
          dateTypeRightList: dateTypeCustomerCommitRightList,
          dateTypeRights: dateTypeCustomerCommitRights,
          finantialModuleRights: finantialModuleCustomerCommitRights,
          menuAccessRightList: menuAccessCustomerCommitRightList,
          statusRightList: statusCustomerCommitRightList,
          supplyVisibilityRights: supplyVisibilityCustomerCommitRights,
          vendorListRights: vendorListCustomerCommitRights,
          widgetAccessRightList: widgetAccessCustomerCommitRightList,
        };

        break;

      case 'Orion_Logistics_Logistic':
        const supplyVisibilityLogistics_LogisticRights: SupplyVisibilityRights = {
          deleteCommits: true,
          /*
          commentsEdit: true,
          commentsView: true,
          managePredefinedComments: true,
          commitsLayout: true,
          dummyCommits: true,
          forecastView: true,
          graphsView: true,*/
          manageCommits: true,
          massChanges: true,
          //manageDummyCommits: true,
          masterDataView: true,
          reviewFlags: true,
          /*notesEdit: true,
          notesView: true,
          reviewFlags: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForCustomer: true,
          //cancelCommits: false,
          deleteCommits: true,
          splitCommits: true,
          mergeCommits: true,
          remergeCommits: true,
          manageSimulationSets: true,
          selectCommitFields: true,*/
        };

        /*Dates types visibility*/
        const dateTypeLogistics_LogisticRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        /*Status visibility*/
        const statusLogisticsRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleLogistics_LogisticRights: CommitsModuleRights = {
          createCommits: true,
          deleteCommits: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          //massChanges: true,
          searchCommits: true,
          selectFields: true,
        };

        const dashboardsLogistics_LogisticRights: DashboardsRights = {
          dashboardView: true,
        };

        const vendorListLogistics_LogisticRights: VendorListRights = {
          /*notificationsSetupView: true,
          //notificationsSetupEdit : false,
          flagEdit: true,
          vendorCreate: true,
          vendorView: true,
          vendorEdit: true,
          vendorDelete: true,
          partNumbersView: true,
          partNumbersEdit: true,
          partNumbersDelete: true,
          buyersView: true,
          buyersManage: true,*/
        };

        const configurationLogistics_LogisticRights: ConfigurationRights = {
          /*virtualGroupsView: true,
          virtualGroupsChange: true,
          vendorCodesView: true,*/
          //searchConfiguration: true
          //vendorCodesChange: false,
          //materialManagementViewsView : false,
        };

        const finantialModuleLogistics_LogisticRights: FinancialModuleRights = {
          /* invoicesView: true,
           invoicesExport: true,
           invoiceReconcilation: true,
           goodReceipts: true,
           quotations: true*/
        };

        const dateTypeLogistics_LogisticRights: DateTypeRights = {
          actualETADate: true,
          etdDate: true,
          instructionEtaDate: true,
          receiveDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true /*GR Date*/,
        };

        const carriersModuleLogisticsRights: CarriersModuleRights = {
          carriersView: true,
          isExpandable: true,
          /*carriersCreate: true,
          carriersEdit: true,
          carriersDelete: true,
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersTransportsCreate: true,
          carriersTransportsDelete: true,
          carriersNotificationsEdit: true,
          carriersNotificationsDelete: true,*/
        };

        const shipmentsModuleLogisticsRights: ShipmentsModuleRights = {
          setInstructionTemplate: true,
          setTemplateEmailBody: true,
          setTemplateEmailSubject: true,
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,
          shipmentsInstructionsView: true,
          shipmentsMrnUpload: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
          /*stocksCreate: false,
          stocksEdit: false,
          stocksDelete: false,
          isExpandable: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const slotsModuleLogisticsRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDownload: true,
          //slotsEdit: true,
          //slotsDelete: true,
          slotsHistoryView: true,
          slotsView: true,
          //slotsUpload: false,
        };

        /*Menu visibility*/
        const menuAccessLogistics_LogisticRightList: string[] = [
          'Carriers',
          'Stocks',
          'Shipments',
          'Shipments Update',
          'Commits',
          'Portfolio',
        ];

        /*Widget visibility*/
        const widgetAccessLogistics_LogisticRightList: string[] = [
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'searchV2',
          'statusMonitor',
          'mypnlist',
          'shipmentsList',
          'portfolioLink',
        ];

        userRights = {
          carriersModuleRights: carriersModuleLogisticsRights,
          commitsModuleRights: commitsModuleLogistics_LogisticRights,
          configurationRights: configurationLogistics_LogisticRights,
          dashboardRights: dashboardsLogistics_LogisticRights,
          dateTypeRightList: dateTypeLogistics_LogisticRightList,
          dateTypeRights: dateTypeLogistics_LogisticRights,
          finantialModuleRights: finantialModuleLogistics_LogisticRights,
          menuAccessRightList: menuAccessLogistics_LogisticRightList,
          shipmentsModuleRights: shipmentsModuleLogisticsRights,
          slotsModuleRights: slotsModuleLogisticsRights,
          statusRightList: statusLogisticsRightList,
          stocksModuleRights: stocksModuleLogisticsRights,
          supplyVisibilityRights: supplyVisibilityLogistics_LogisticRights,
          vendorListRights: vendorListLogistics_LogisticRights,
          widgetAccessRightList: widgetAccessLogistics_LogisticRightList,
        };

        break;

      case 'Orion_Logistics_Analyst':
        const supplyVisibilityLogisticsAnalystRights: SupplyVisibilityRights = {
          deleteCommits: true,
          /*
          commentsEdit: true,
          commentsView: true,
          managePredefinedComments: true,
          commitsLayout: true,
          dummyCommits: true,
          forecastView: true,
          graphsView: true,*/
          manageCommits: true,
          massChanges: true,
          //manageDummyCommits: true,
          masterDataView: true,
          reviewFlags: true,
          /*notesEdit: true,
          notesView: true,
          reviewFlags: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForSupplier: true,
          reviewFlagsForCustomer: true,
          //cancelCommits: false,
          deleteCommits: true,
          splitCommits: true,
          mergeCommits: true,
          remergeCommits: true,
          manageSimulationSets: true,
          selectCommitFields: true,*/
        };

        const carriersModuleLogisticsAnalystRights: CarriersModuleRights = {
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersCreate: true,
          carriersDelete: true,
          carriersEdit: true,
          carriersNotificationsDelete: true,
          carriersNotificationsEdit: true,
          carriersTransportsCreate: true,
          carriersTransportsDelete: true,
          carriersView: true,
          isExpandable: true,
        };

        const shipmentsModuleLogisticsAnalystRights: ShipmentsModuleRights = {
          setInstructionTemplate: true,
          setTemplateEmailBody: true,
          setTemplateEmailSubject: true,
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,
          shipmentsInstructionsView: true,
          shipmentsMrnUpload: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsAnalystRights: StocksModuleRights = {
          isExpandable: true,
          stocksCreate: true,
          stocksDelete: true,
          stocksEdit: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true,
          stocksView: true,
        };

        const commitsModuleLogisticsAnalystRights: CommitsModuleRights = {
          createCommits: true,
          defaultFieldsSetup: true,
          deleteCommits: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          //massChanges: true,
          searchCommits: true,
          selectFields: true,
        };

        /*Dates types visibility*/
        const dateTypeLogisticsAnalystRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeLogisticsAnalystRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsAnalystRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Widget visibility*/
        const widgetAccessLogistics_AnalystRightList: string[] = [
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'searchV2',
          'statusMonitor',
          'mypnlist',
          'portfolioLink',
          'shipmentsList',
        ];

        /*Menu visibility*/
        const menuAccessLogistics_Logistic_Analyst_RightList: string[] = [
          'Carriers',
          'Stocks',
          'Shipments',
          'Shipments Update',
          'SCM Default Fields',
          'Financial Module Default Fields',
          'Logistic Default Fields',
          'Commits',
          'Portfolio',
        ];

        const slotsStorageModuleLogisticsAnalystRights: SlotsStorageModuleRights = {
          /*
          slotsStorageView: true,
          slotsStorageCreate: true,
          slotsStorageEdit: true,
          slotsStorageDelete: true,
          isExpandable: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursEdit: true,
          slotStorageWorkingHoursDelete: true
          */
        };

        const slotsModuleLogisticsAnalystRights: SlotsModuleRights = {
          slotsCreate: true,
          /*slotsEdit: true,
          slotsDelete: true,
          slotsHistoryView: true,
          slotsDownload: true,
          slotsUpload: true*/
          slotsDownload: true,
          slotsHistoryView: true,
          slotsView: true,
        };

        userRights = {
          carriersModuleRights: carriersModuleLogisticsAnalystRights,
          commitsModuleRights: commitsModuleLogisticsAnalystRights,
          dateTypeRightList: dateTypeLogisticsAnalystRightList,
          dateTypeRights: dateTypeLogisticsAnalystRights,
          menuAccessRightList: menuAccessLogistics_Logistic_Analyst_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsAnalystRights,
          slotsModuleRights: slotsModuleLogisticsAnalystRights,
          slotsStorageModuleRights: slotsStorageModuleLogisticsAnalystRights,
          statusRightList: statusLogisticsAnalystRightList,
          stocksModuleRights: stocksModuleLogisticsAnalystRights,
          supplyVisibilityRights: supplyVisibilityLogisticsAnalystRights,
          widgetAccessRightList: widgetAccessLogistics_AnalystRightList,
        };

        break;

      case 'Orion_Logistics_Carrier':
        const carriersModuleLogisticsCarrierRights: CarriersModuleRights = {
          //carriersCreate: true,
          //carriersEdit: true,
          //carriersDelete: true,
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersNotificationsDelete: true,
          //carriersTransportsCreate: true,
          //carriersTransportsDelete: true,
          carriersNotificationsEdit: true,
          carriersView: true,
          isExpandable: true,
        };

        const shipmentsModuleLogisticsCarrierRights: ShipmentsModuleRights = {
          /*shipmentsFlagsEdit: false,*/
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsRegenerateTemplate: true,
          /*shipmentInternalDocumentsView: false,
          shipmentInternalDocumentsUpload: false,
          shipmentInternalDocumentsDownload: false,
          shipmentInternalDocumentsDelete: false,*/
          shipmentsInstructionsView: true,
          shipmentsMrnUpload: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsCarrierRights: StocksModuleRights = {
          /*stocksView: true,
          stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const commitsModuleLogisticsCarrierRights: CommitsModuleRights = {
          documentsView: true,
          downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
        };

        /*Dates types visibility*/
        const dateTypeLogisticsCarrierRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeLogisticsCarrierRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsCarrierRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const slotsModuleLogisticsCarrierRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsView: true,
          //slotsUpload: false,
        };

        /*Widget visibility*/
        const widgetAccessLogistics_CarrierRightList: string[] = ['shipmentsList', 'slotList'];

        /*Menu visibility*/
        const menuAccessLogistics_Logistic_Carrier_RightList: string[] = ['Carriers', 'Shipments'];

        userRights = {
          carriersModuleRights: carriersModuleLogisticsCarrierRights,
          commitsModuleRights: commitsModuleLogisticsCarrierRights,
          dateTypeRightList: dateTypeLogisticsCarrierRightList,
          dateTypeRights: dateTypeLogisticsCarrierRights,
          menuAccessRightList: menuAccessLogistics_Logistic_Carrier_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsCarrierRights,
          slotsModuleRights: slotsModuleLogisticsCarrierRights,
          statusRightList: statusLogisticsCarrierRightList,
          stocksModuleRights: stocksModuleLogisticsCarrierRights,
          widgetAccessRightList: widgetAccessLogistics_CarrierRightList,
        };

        break;

      case 'Orion_Logistics_Warehouse':
        const carriersModuleLogisticsWarehouseRights: CarriersModuleRights = {
          /*carriersView: true,
          //carriersCreate: true,
          //carriersEdit: true,
          //carriersDelete: true,
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          //carriersTransportsCreate: true,
          //carriersTransportsDelete: true,
          carriersNotificationsEdit: true,
          carriersNotificationsDelete: true,*/
        };

        const shipmentsModuleLogisticsWarehouseRights: ShipmentsModuleRights = {
          //shipmentsFlagsEdit: false,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsView: true,
          shipmentsHistoryView: true,
          /*shipmentInternalDocumentsView: false,
          shipmentInternalDocumentsUpload: false,
          shipmentInternalDocumentsDownload: false,
          shipmentInternalDocumentsDelete: false,*/
          shipmentsInstructionsView: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsWarehouseRights: StocksModuleRights = {
          isExpandable: true,
          /*stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,*/
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true,
          stocksView: true,
        };

        const commitsModuleLogisticsWarehouseRights: CommitsModuleRights = {
          /*downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
          documentsView: true,*/
        };

        /*Dates types visibility*/
        const dateTypeLogisticsWarehouseRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
          'shipmentsList',
        ];

        const dateTypeLogisticsWarehouseRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsWarehouseRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Widget visibility*/
        const widgetAccessLogistics_WarehouseRightList: string[] = ['shipmentsList'];

        /*Menu visibility*/
        const menuAccessLogistics_Logistic_Warehouse_RightList: string[] = ['Shipments'];

        const slotsStorageModuleLogisticsWarehouseRights: SlotsStorageModuleRights = {
          slotsStorageView: true,
          /*slotsStorageCreate: false,
          slotsStorageEdit: false,
          slotsStorageDelete: false,
          isExpandable: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursEdit: true,
          slotStorageWorkingHoursDelete: true*/
        };

        const slotsModuleLogisticsWarehouseRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDownload: true,
          //slotsEdit: true,
          //slotsDelete: true,
          slotsHistoryView: true,
          slotsView: true,
          //slotsUpload: true
        };

        userRights = {
          carriersModuleRights: carriersModuleLogisticsWarehouseRights,
          commitsModuleRights: commitsModuleLogisticsWarehouseRights,
          dateTypeRightList: dateTypeLogisticsWarehouseRightList,
          dateTypeRights: dateTypeLogisticsWarehouseRights,
          menuAccessRightList: menuAccessLogistics_Logistic_Warehouse_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsWarehouseRights,
          slotsModuleRights: slotsModuleLogisticsWarehouseRights,
          slotsStorageModuleRights: slotsStorageModuleLogisticsWarehouseRights,
          statusRightList: statusLogisticsWarehouseRightList,
          stocksModuleRights: stocksModuleLogisticsWarehouseRights,
          widgetAccessRightList: widgetAccessLogistics_WarehouseRightList,
        };

        break;

      case 'Orion_Logistics_Declarant':
        const carriersModuleLogisticsDeclarantRights: CarriersModuleRights = {
          /*carriersView: true,
          //carriersCreate: true,
          //carriersEdit: true,
          //carriersDelete: true,
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          //carriersTransportsCreate: true,
          //carriersTransportsDelete: true,
          carriersNotificationsEdit: true,
          carriersNotificationsDelete: true,*/
        };

        const shipmentsModuleLogisticsDeclarantRights: ShipmentsModuleRights = {
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          //shipmentsDocumentsDelete: false,
          shipmentsDocumentsDownload: true,
          //shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsHistoryView: true,
          /*shipmentInternalDocumentsView: false,
          shipmentInternalDocumentsUpload: false,
          shipmentInternalDocumentsDownload: false,
          shipmentInternalDocumentsDelete: false,*/
          shipmentsInstructionsView: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsDeclarantRights: StocksModuleRights = {
          /*stocksView: true,
          stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const commitsModuleLogisticsDeclarantRights: CommitsModuleRights = {
          /*downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
          documentsView: true,*/
        };

        /*Dates types visibility*/
        const dateTypeLogisticsDeclarantRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeLogisticsDeclarantRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsDeclarantRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const slotsModuleLogisticsDeclarantRights: SlotsModuleRights = {
          slotsDownload: true,
          /*slotsCreate: false,
          slotsEdit: false,
          slotsDelete: false,*/
          slotsHistoryView: true,
          slotsView: true,
          //slotsUpload: false,
        };

        /*Widget visibility*/
        const widgetAccessLogistics_DeclarantRightList: string[] = ['shipmentsList', 'slotList'];

        /*Menu visibility*/
        const menuAccessLogistics_Logistic_Declarant_RightList: string[] = ['Shipments'];

        userRights = {
          carriersModuleRights: carriersModuleLogisticsDeclarantRights,
          commitsModuleRights: commitsModuleLogisticsDeclarantRights,
          dateTypeRightList: dateTypeLogisticsDeclarantRightList,
          dateTypeRights: dateTypeLogisticsDeclarantRights,
          menuAccessRightList: menuAccessLogistics_Logistic_Declarant_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsDeclarantRights,
          slotsModuleRights: slotsModuleLogisticsDeclarantRights,
          statusRightList: statusLogisticsDeclarantRightList,
          stocksModuleRights: stocksModuleLogisticsDeclarantRights,
          widgetAccessRightList: widgetAccessLogistics_DeclarantRightList,
        };

        break;

      case 'Orion_Logistics_Readonly':
        const carriersModuleLogisticsReadonlyRights: CarriersModuleRights = {
          carriersView: true,
          isExpandable: true,
          /*//carriersCreate: true,
          //carriersEdit: true,
          //carriersDelete: true,
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          //carriersTransportsCreate: true,
          //carriersTransportsDelete: true,
          carriersNotificationsEdit: true,
          carriersNotificationsDelete: true,*/
        };

        const shipmentsModuleLogisticsReadonlyRights: ShipmentsModuleRights = {
          //shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          //shipmentsDocumentsUpload: false,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsView: true,
          /*shipmentsDocumentsDelete: false,
          shipmentInternalDocumentsView: false,
          shipmentInternalDocumentsUpload: false,
          shipmentInternalDocumentsDownload: false,
          shipmentInternalDocumentsDelete: false,*/
          shipmentsFlagsView: true,
          shipmentsView: true,
          //shipmentsFlagsEdit: false,
        };

        const stocksModuleLogisticsReadonlyRights: StocksModuleRights = {
          /*stocksView: true,
          stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const commitsModuleLogisticsReadonlyRights: CommitsModuleRights = {
          /*downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
          documentsView: true,*/
        };

        /*Dates types visibility*/
        const dateTypeLogisticsReadOnlyRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeLogisticsReadOnlyRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsReadOnlyRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const slotsModuleLogisticsReadOnlyRights: SlotsModuleRights = {
          slotsDownload: true,
          /*slotsCreate: false,
          slotsEdit: false,
          slotsDelete: false,*/
          slotsHistoryView: true,
          slotsView: true,
          //slotsUpload: false,
        };

        /*Widget visibility*/
        const widgetAccessLogistics_ReadonlyRightList: string[] = [];

        /*Menu visibility*/
        const menuAccessLogistics_Logistic_Readonly_RightList: string[] = [
          'Shipments',
          'Shipments Update',
          'Carriers',
        ];

        userRights = {
          carriersModuleRights: carriersModuleLogisticsReadonlyRights,
          commitsModuleRights: commitsModuleLogisticsReadonlyRights,
          dateTypeRightList: dateTypeLogisticsReadOnlyRightList,
          dateTypeRights: dateTypeLogisticsReadOnlyRights,
          menuAccessRightList: menuAccessLogistics_Logistic_Readonly_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsReadonlyRights,
          slotsModuleRights: slotsModuleLogisticsReadOnlyRights,
          statusRightList: statusLogisticsReadOnlyRightList,
          stocksModuleRights: stocksModuleLogisticsReadonlyRights,
          widgetAccessRightList: widgetAccessLogistics_ReadonlyRightList,
        };
        break;

      case 'Orion_Finance_Analyst':
        const supplyVisibilityFinanceAnalystRights: SupplyVisibilityRights = {};

        const commitsModuleFinanceAnalystRights: CommitsModuleRights = {
          documentsView: true,
        };

        const dashboardsFinanceAnalystRights: DashboardsRights = {};

        const vendorListFinanceAnalystRights: VendorListRights = {};

        const configurationFinanceAnalystRights: ConfigurationRights = {
          //searchConfiguration: true
        };

        const finantialModuleFinanceAnalystRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          selectFields: true,
        };

        const dateTypeFinanceAnalystRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };
        /*Dates types visibility*/
        const dateTypeFinanceAnalystRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusFinanceAnalystRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Menu visibility*/
        const menuAccess_Finance_Analyst_RightList: string[] = [
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'SCM Default Fields',
          'Financial Module Default Fields',
          'Logistic Default Fields',
        ];

        /*Widget visibility*/
        const widgetAccessFinanceAnalystRightList: string[] = [];

        userRights = {
          commitsModuleRights: commitsModuleFinanceAnalystRights,
          configurationRights: configurationFinanceAnalystRights,
          dashboardRights: dashboardsFinanceAnalystRights,
          dateTypeRightList: dateTypeFinanceAnalystRightList,
          dateTypeRights: dateTypeFinanceAnalystRights,
          finantialModuleRights: finantialModuleFinanceAnalystRights,
          menuAccessRightList: menuAccess_Finance_Analyst_RightList,
          statusRightList: statusFinanceAnalystRightList,
          supplyVisibilityRights: supplyVisibilityFinanceAnalystRights,
          vendorListRights: vendorListFinanceAnalystRights,
          widgetAccessRightList: widgetAccessFinanceAnalystRightList,
        };

        break;

      case 'Orion_SCM_Sourcing':
        const supplyVisibilitySourcingRights: SupplyVisibilityRights = {
          commentsView: true,
          commitsLayout: true,
          forecastView: true,
          graphsView: true,
          manageSimulationSets: true,
          masterDataView: true,
          reviewFlags: true,
          selectCommitFields: true,
        };

        /*Dates types visibility*/
        const dateTypeSourcingRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusSourcingRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleSourcingRights: CommitsModuleRights = {
          documentsView: true,
          downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
        };

        const dashboardsSourcingRights: DashboardsRights = {
          //dashboardEdit : false,
          dashboardView: true,
          //sharedDashboards : false,
        };

        const vendorListSourcingRights: VendorListRights = {
          notificationsSetupView: true,
          partNumbersView: true,
          vendorView: true,
        };

        const configurationSourcingRights: ConfigurationRights = {
          //searchConfiguration: true
          /*virtualGroupsView: false,
          virtualGroupsChange: false,
          vendorCodesView: false,
          vendorCodesChange: false,
          materialManagementViewsView : false,*/
        };

        const finantialModuleSourcingRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          selectFields: true,
        };

        const dateTypeSourcingRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };

        /*Menu visibility*/
        const menuAccessSourcingRightList: string[] = [
          'Commits',
          'My Portfolio' /*'Vendors', 'Carriers',*/,
          /*'Contacts', 'PartNumbers',*/ 'QAP',
          'RFQ',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Audit Log',
        ];

        /*Widget visibility*/
        const widgetAccessSourcingRightList: string[] = [
          'statusMonitor',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
        ];

        userRights = {
          commitsModuleRights: commitsModuleSourcingRights,
          configurationRights: configurationSourcingRights,
          dashboardRights: dashboardsSourcingRights,
          dateTypeRightList: dateTypeSourcingRightList,
          dateTypeRights: dateTypeSourcingRights,
          finantialModuleRights: finantialModuleSourcingRights,
          menuAccessRightList: menuAccessSourcingRightList,
          statusRightList: statusSourcingRightList,
          supplyVisibilityRights: supplyVisibilitySourcingRights,
          vendorListRights: vendorListSourcingRights,
          widgetAccessRightList: widgetAccessSourcingRightList,
        };

        break;

      case 'Orion_SCM_Master_Buyer':
        const supplyVisibilityMasterBuyerRights: SupplyVisibilityRights = {
          commentsEdit: true,
          commentsView: true,
          commitsLayout: true,
          //cancelCommits: false,
          deleteCommits: true,
          dummyCommits: true,
          forecastView: true,
          graphsView: true,
          manageCommits: true,
          manageDummyCommits: true,
          managePredefinedComments: true,
          manageSimulationSets: true,
          massChanges: true,
          masterDataView: true,
          mergeCommits: true,
          notesEdit: true,
          notesView: true,
          remergeCommits: true,
          reviewFlags: true,
          reviewFlagsEdit: true,
          reviewFlagsForBuyer: true,
          reviewFlagsForBuyerEdit: true,
          reviewFlagsForCustomer: true,
          reviewFlagsForSupplier: true,
          selectCommitFields: true,
          splitCommits: true,
        };

        /*Dates types visibility*/
        const dateTypeMasterBuyerRightList: string[] = [
          'sapDeliveryDate',
          'etdDate',
          'receiveDate', //grdate
          'triggerDate',
          'plannedOrderStatus',
          'createDate',
        ];

        /*Status visibility*/
        const statusMasterBuyerRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        const commitsModuleMasterBuyerRights: CommitsModuleRights = {
          createCommits: true,
          //cancelCommits: false,
          deleteCommits: true,
          deletePLO: true,
          documentsCreate: true,
          documentsDelete: true,
          documentsView: true,
          downloadExcel: true,
          editCommits: true,
          layoutChanges: true,
          lockCommit: true,
          massChanges: true,
          mergeCommits: true,
          remergeCommits: true,
          searchCommits: true,
          selectFields: true,
          splitCommits: true,
        };

        const dashboardsMasterBuyerRights: DashboardsRights = {
          //sharedDashboards : false,
        };

        const vendorListMasterBuyerRights: VendorListRights = {
          buyersManage: true,
          buyersView: true,
          //notificationsSetupEdit : false,
          flagEdit: true,
          notificationsSetupView: true,
          partNumbersDelete: true,
          partNumbersEdit: true,
          partNumbersView: true,
          vendorCreate: true,
          vendorDelete: true,
          vendorEdit: true,
          vendorView: true,
        };

        const configurationMasterBuyerRights: ConfigurationRights = {
          reviewVisibilityForBuyer: true,
          searchConfiguration: true,
          vendorCodesView: true,
          virtualGroupsChange: true,
          virtualGroupsView: true,
          //vendorCodesChange: false,
          //materialManagementViewsView : false,
        };

        const shipmentsModuleMasterBuyerRights: ShipmentsModuleRights = {
          //shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          //shipmentsDocumentsUpload: true,
          //shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsView: true,
          //shipmentInternalDocumentsView: true,
          //shipmentInternalDocumentsUpload: true,
          //shipmentInternalDocumentsDownload: true,
          //shipmentInternalDocumentsDelete: true,
          shipmentsFlagsView: true,
          shipmentsView: true,
          //shipmentsFlagsEdit: true,
        };

        const stocksModuleMasterBuyerRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
        };

        const finantialModuleMasterBuyerRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: true,
          selectFields: true,
        };

        const dateTypeMasterBuyerRights: DateTypeRights = {
          createDate: true,
          etdDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          sapDeliveryDate: true /*Final ETA*/,
          triggerDate: true,
        };

        /*Menu visibility*/
        const menuAccessMasterBuyerRightList: string[] = [
          'Commits',
          'PN Groups',
          'Vendor Codes Rights',
          'My Portfolio',
          'Vendors',
          'Carriers',
          'Shipments',
          'Stocks',
          'Contacts',
          'PartNumbers',
          'QAP',
          'RFQ',
          'Portfolio',
          'NotesAndComments',
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Audit Log',
        ];

        /*Widget visibility*/
        const widgetAccessMasterBuyerRightList: string[] = [
          'statusMonitor',
          'qapLink',
          'pnGroupsLink',
          'vendorcodeRightsLink',
          'fileUpload',
          'mypnlist',
          'ploSync',
          'configurationLink',
          'searchV2',
          'waterfall',
          'commitList',
          'commitsLink',
          'smartPNList',
          'filterWidget',
          'circularChart',
          'simplePNList',
          'invoiceList',
          'portfolioLink',
        ];

        userRights = {
          commitsModuleRights: commitsModuleMasterBuyerRights,
          configurationRights: configurationMasterBuyerRights,
          dashboardRights: dashboardsMasterBuyerRights,
          dateTypeRightList: dateTypeMasterBuyerRightList,
          dateTypeRights: dateTypeMasterBuyerRights,
          finantialModuleRights: finantialModuleMasterBuyerRights,
          menuAccessRightList: menuAccessMasterBuyerRightList,
          shipmentsModuleRights: shipmentsModuleMasterBuyerRights,
          statusRightList: statusMasterBuyerRightList,
          stocksModuleRights: stocksModuleMasterBuyerRights,
          supplyVisibilityRights: supplyVisibilityMasterBuyerRights,
          vendorListRights: vendorListMasterBuyerRights,
          widgetAccessRightList: widgetAccessMasterBuyerRightList,
        };

        break;

      case 'Orion_Warehouse_Analyst':
        const carriersModuleWarehouseAnalystRights: CarriersModuleRights = {
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersCreate: true,
          carriersDelete: true,
          carriersEdit: true,
          carriersNotificationsDelete: true,
          carriersNotificationsEdit: true,
          carriersTransportsCreate: true,
          carriersTransportsDelete: true,
          carriersView: true,
        };

        const shipmentsModuleWarehouseAnalystRights: ShipmentsModuleRights = {
          //shipmentsFlagsEdit: false,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsView: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsView: true,
          shipmentsView: true,
        };

        const stocksModuleWarehouseAnalystRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
          /*stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const commitsModuleWarehouseAnalystRights: CommitsModuleRights = {
          defaultFieldsSetup: true,
          /*downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
          documentsView: true,*/
        };

        /*Dates types visibility*/
        const dateTypeWarehouseAnalystRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeWarehouseAnalystRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusWarehouseAnalystRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Widget visibility*/
        const widgetAccess_WarehouseAnalystRightList: string[] = ['shipmentsList', 'slotList'];

        /*Menu visibility*/
        const menuAccessLogistics_WarehouseAnalyst_RightList: string[] = [
          'Shipments',
          'Logistic Default Fields',
          'Portfolio',
        ];

        const slotsStorageModuleWarehouseAnalystRights: SlotsStorageModuleRights = {
          isExpandable: true,
          slotsStorageCreate: true,
          slotsStorageDelete: true,
          slotsStorageEdit: true,
          slotsStorageView: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursDelete: true,
          slotStorageWorkingHoursEdit: true,
        };

        const slotsModuleWarehouseAnalystRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsUpload: true,
          slotsView: true,
        };

        userRights = {
          carriersModuleRights: carriersModuleWarehouseAnalystRights,
          commitsModuleRights: commitsModuleWarehouseAnalystRights,
          dateTypeRightList: dateTypeWarehouseAnalystRightList,
          dateTypeRights: dateTypeWarehouseAnalystRights,
          menuAccessRightList: menuAccessLogistics_WarehouseAnalyst_RightList,
          shipmentsModuleRights: shipmentsModuleWarehouseAnalystRights,
          slotsModuleRights: slotsModuleWarehouseAnalystRights,
          slotsStorageModuleRights: slotsStorageModuleWarehouseAnalystRights,
          statusRightList: statusWarehouseAnalystRightList,
          stocksModuleRights: stocksModuleWarehouseAnalystRights,
          widgetAccessRightList: widgetAccess_WarehouseAnalystRightList,
        };

        break;

      case 'Orion_Logistics_Slot':
        const carriersModuleLogisticsSlotRights: CarriersModuleRights = {
          carriersContactsCreate: true,
          carriersContactsDelete: true,
          carriersCreate: true,
          carriersDelete: true,
          carriersEdit: true,
          carriersNotificationsDelete: true,
          carriersNotificationsEdit: true,
          carriersTransportsCreate: true,
          carriersTransportsDelete: true,
          carriersView: true,
        };

        const shipmentsModuleLogisticsSlotRights: ShipmentsModuleRights = {
          //shipmentsFlagsEdit: false,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsEdit: true,
          shipmentsFlagsView: true,
          shipmentsHistoryView: true,
          shipmentsInstructionsView: true,
          shipmentsView: true,
        };

        const stocksModuleLogisticsSlotRights: StocksModuleRights = {
          isExpandable: true,
          stocksView: true,
          /*stocksCreate: true,
          stocksEdit: true,
          stocksDelete: true,
          stocksNotificationsCreate: true,
          stocksNotificationsDelete: true*/
        };

        const commitsModuleLogisticsSlotRights: CommitsModuleRights = {
          /*downloadExcel: true,
          layoutChanges: true,
          searchCommits: true,
          selectFields: true,
          documentsView: true,*/
        };

        /*Dates types visibility*/
        const dateTypeLogisticsSlotRightList: string[] = [
          'sapDeliveryDate',
          'etaDate',
          'deliveryDate',
          'etdDate',
          'slotDate',
          'requestDate',
          'triggerDate',
          'recomitRequestDate',
          'eddDate',
          'instructionEtaDate',
          'otmReceivedDate',
          'actualETADate',
          'receiveDate',
          'plannedOrderStatus',
        ];

        const dateTypeLogisticsSlotRights: DateTypeRights = {
          actualETADate: true,
          deliveryDate: true,
          eddDate: true,
          etaDate: true,
          etdDate: true,
          instructionEtaDate: true,
          otmReceivedDate: true,
          plannedOrderStatus: true,
          receiveDate: true /*GR Date*/,
          recomitRequestDate: true,
          requestDate: true,
          sapDeliveryDate: true /*Final ETA*/,
          slotDate: true,
          triggerDate: true,
        };

        /*Status visibility*/
        const statusLogisticsSlotRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Widget visibility*/
        const widgetAccess_LogisticsSlotRightList: string[] = ['shipmentsList', 'slotList'];

        /*Menu visibility*/
        const menuAccessLogistics_LogisticsSlot_RightList: string[] = ['Shipments', 'Portfolio'];

        const slotsStorageModuleLogisticsSlotRights: SlotsStorageModuleRights = {
          isExpandable: true,
          slotsStorageView: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursDelete: true,
          slotStorageWorkingHoursEdit: true,
        };

        const slotsModuleLogisticsSlotRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsUpload: true,
          slotsView: true,
        };

        userRights = {
          carriersModuleRights: carriersModuleLogisticsSlotRights,
          commitsModuleRights: commitsModuleLogisticsSlotRights,
          dateTypeRightList: dateTypeLogisticsSlotRightList,
          dateTypeRights: dateTypeLogisticsSlotRights,
          menuAccessRightList: menuAccessLogistics_LogisticsSlot_RightList,
          shipmentsModuleRights: shipmentsModuleLogisticsSlotRights,
          slotsModuleRights: slotsModuleLogisticsSlotRights,
          slotsStorageModuleRights: slotsStorageModuleLogisticsSlotRights,
          statusRightList: statusLogisticsSlotRightList,
          stocksModuleRights: stocksModuleLogisticsSlotRights,
          widgetAccessRightList: widgetAccess_LogisticsSlotRightList,
        };

        break;

      case 'Orion_Multi':
        const dashboardsMultiRights: DashboardsRights = {
          sharedDashboards: true,
        };

        const finantialModuleMultiRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          quotations: true,
          selectFields: true,
        };

        const shipmentsModuleMultiRights: ShipmentsModuleRights = {
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsFlagsEdit: true,
          /*shipmentsInstructionsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,*/
          shipmentsFlagsView: true,
          shipmentsView: true,
          //setInstructionTemplate: true,
          //setTemplateEmailSubject: true,
          //setTemplateEmailBody: true,
        };

        /*Menu visibility*/
        const menuAccessMultiRightList: string[] = [
          'Invoicing',
          'Goods Receipts',
          'Financial Module Default Fields',
          'Shipments',
          'Shipments Update',
          'Logistic Default Fields',
        ];

        /*Widget visibility*/
        const widgetAccessMultiRightList: string[] = ['invoiceList', 'shipmentsList'];

        userRights = {
          dashboardRights: dashboardsMultiRights,
          finantialModuleRights: finantialModuleMultiRights,
          menuAccessRightList: menuAccessMultiRightList,
          shipmentsModuleRights: shipmentsModuleMultiRights,
          widgetAccessRightList: widgetAccessMultiRightList,
        };
        break;

      case 'Orion_AI':
      case 'Orion_AI_Analyst':
        const supplyVisibilityAIRights: SupplyVisibilityRights = {};

        /*Dates types visibility*/
        const dateTypeAIRightList: string[] = [];

        /*Status visibility*/
        const statusAIRightList: string[] = [];

        const commitsModuleAIRights: CommitsModuleRights = {};

        const dashboardsAIRights: DashboardsRights = {};

        const vendorListAIRights: VendorListRights = {};

        const configurationAIRights: ConfigurationRights = {};

        const finantialModuleAIRights: FinancialModuleRights = {};

        const dateTypeAIRights: DateTypeRights = {};

        /*Menu visibility*/
        const menuAccessAIRightList: string[] = ['A.I. Chat'];

        /*Widget visibility*/
        const widgetAccessAIRightList: string[] = ['aiLink'];

        userRights = {
          commitsModuleRights: commitsModuleAIRights,
          configurationRights: configurationAIRights,
          dashboardRights: dashboardsAIRights,
          dateTypeRightList: dateTypeAIRightList,
          dateTypeRights: dateTypeAIRights,
          finantialModuleRights: finantialModuleAIRights,
          menuAccessRightList: menuAccessAIRightList,
          statusRightList: statusAIRightList,
          supplyVisibilityRights: supplyVisibilityAIRights,
          vendorListRights: vendorListAIRights,
          widgetAccessRightList: widgetAccessAIRightList,
        };

        break;

      case 'Orion_JUSDA':
        const dashboardsJusdaRights: DashboardsRights = {
          sharedDashboards: true,
        };

        const shipmentsModuleJusdaRights: ShipmentsModuleRights = {
          setInstructionTemplate: true,
          setTemplateEmailBody: true,
          setTemplateEmailSubject: true,
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,
          shipmentsView: true,
        };

        const slotsStorageModuleJusdaRights: SlotsStorageModuleRights = {
          isExpandable: true,
          slotsStorageCreate: true,
          slotsStorageDelete: true,
          slotsStorageEdit: true,
          slotsStorageView: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursDelete: true,
        };

        const slotsModuleJusdaRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsUpload: true,
          slotsView: true,
        };

        /*Menu visibility*/
        const menuAccessJusdaRightList: string[] = ['Shipments', 'Logistic Default Fields'];

        /*Widget visibility*/
        const widgetAccessJusdaRightList: string[] = ['slotList', 'shipmentsList'];

        userRights = {
          dashboardRights: dashboardsJusdaRights,
          menuAccessRightList: menuAccessJusdaRightList,
          shipmentsModuleRights: shipmentsModuleJusdaRights,
          slotsModuleRights: slotsModuleJusdaRights,
          slotsStorageModuleRights: slotsStorageModuleJusdaRights,
          widgetAccessRightList: widgetAccessJusdaRightList,
        };
        break;

      case 'Orion_CTP':
        const dashboardsCTPRights: DashboardsRights = {
          sharedDashboards: true,
        };

        const shipmentsModuleCTPRights: ShipmentsModuleRights = {
          setInstructionTemplate: true,
          setTemplateEmailBody: true,
          setTemplateEmailSubject: true,
          shipmentInternalDocumentsDelete: true,
          shipmentInternalDocumentsDownload: true,
          shipmentInternalDocumentsUpload: true,
          shipmentInternalDocumentsView: true,
          shipmentPDFDownload: true,
          shipmentsCreate: false,
          //shipmentsEdit: true,
          //shipmentsDelete: true,
          shipmentsDeliveriesDetailsView: true,
          shipmentsDocumentsDelete: true,
          shipmentsDocumentsDownload: true,
          shipmentsDocumentsUpload: true,
          shipmentsDocumentsView: true,
          shipmentsFlagsEdit: true,
          shipmentsFlagsView: true,
          shipmentsGenerateInstructions: true,
          shipmentsInstructionsEditDetails: true,
          shipmentsInstructionsEditTemplate: true,
          shipmentsInstructionsRegenerateTemplate: true,
          shipmentsView: true,
        };

        const slotsStorageModuleCTPRights: SlotsStorageModuleRights = {
          isExpandable: true,
          slotsStorageCreate: true,
          slotsStorageDelete: true,
          slotsStorageEdit: true,
          slotsStorageView: true,
          slotStorageContactCreate: true,
          slotStorageContactDelete: true,
          slotStorageWorkingHoursCreate: true,
          slotStorageWorkingHoursDelete: true,
        };

        const slotsModuleCTPRights: SlotsModuleRights = {
          slotsCreate: true,
          slotsDelete: true,
          slotsDownload: true,
          slotsEdit: true,
          slotsHistoryView: true,
          slotsUpload: true,
          slotsView: true,
        };

        /*Menu visibility*/
        const menuAccessCTPRightList: string[] = ['Shipments', 'Logistic Default Fields'];

        /*Widget visibility*/
        const widgetAccessCTPRightList: string[] = ['slotList', 'shipmentsList'];

        userRights = {
          dashboardRights: dashboardsCTPRights,
          menuAccessRightList: menuAccessCTPRightList,
          shipmentsModuleRights: shipmentsModuleCTPRights,
          slotsModuleRights: slotsModuleCTPRights,
          slotsStorageModuleRights: slotsStorageModuleCTPRights,
          widgetAccessRightList: widgetAccessCTPRightList,
        };
        break;

      case 'Orion_Finance_Accountant':
        const supplyVisibilityFinanceAccountantRights: SupplyVisibilityRights = {};

        const commitsModuleFinanceAccountantRights: CommitsModuleRights = {
          documentsView: true,
        };

        const dashboardsFinanceAccountantRights: DashboardsRights = {};

        const vendorListFinanceAccountantRights: VendorListRights = {};

        const configurationFinanceAccountantRights: ConfigurationRights = {
          //searchConfiguration: true
        };

        const finantialModuleFinanceAccountantRights: FinancialModuleRights = {
          goodReceipts: true,
          invoiceReconcilation: true,
          invoicesExport: true,
          invoicesView: true,
          selectFields: true,
        };

        const dateTypeFinanceRights: DateTypeRights = {
          sapDeliveryDate: true /*Final ETA*/,
        };
        /*Dates types visibility*/
        const dateTypeFinanceRightList: string[] = ['sapDeliveryDate'];

        /*Status visibility*/
        const statusFinanceRightList: string[] = [
          'Delivered',
          'New',
          'Initial triggers',
          'Waiting',
          'Partial',
        ];

        /*Menu visibility*/
        const menuAccessFinanceRightList: string[] = [
          'Invoicing',
          'Goods Receipts',
          'Invoice Reconciliation',
          'Portfolio',
        ];

        /*Widget visibility*/
        const widgetAccessFinanceRightList: string[] = [];

        userRights = {
          commitsModuleRights: commitsModuleFinanceAccountantRights,
          configurationRights: configurationFinanceAccountantRights,
          dashboardRights: dashboardsFinanceAccountantRights,
          dateTypeRightList: dateTypeFinanceRightList,
          dateTypeRights: dateTypeFinanceRights,
          finantialModuleRights: finantialModuleFinanceAccountantRights,
          menuAccessRightList: menuAccessFinanceRightList,
          statusRightList: statusFinanceRightList,
          supplyVisibilityRights: supplyVisibilityFinanceAccountantRights,
          vendorListRights: vendorListFinanceAccountantRights,
          widgetAccessRightList: widgetAccessFinanceRightList,
        };
    }
    return userRights;
  }
  public static readonly fullAccessRoles = [
    'Orion_SCM_Analyst',
    'Orion_SCM_Buyer',
    'Orion_Logistics_Logistic',
    'Orion_Administrator',
    'Orion_SCM_Master_Buyer',
    'Orion_Logistics_Analyst',
    'Orion_Warehouse_Analyst',
  ];

  public static readonly limitedAccessRoles = [
    'Orion_SCM_Supplier_Commit',
    'Orion_SCM_Supplier_BS',
    'Orion_SCM_Supplier_No_Commit',
    'Orion_SCM_Customer',
    'Orion_SCM_ReadOnly',
    'Orion_SCM_Supplier_ROP',
    'Orion_SCM_Engineering',
    'Orion_SCM_Customer_Commit',
    'Orion_SCM_Sourcing',
    'Orion_AI',
    'Orion_Logistics_Carrier',
    'Orion_Logistics_Warehouse',
    'Orion_Logistics_Declarant',
    'Orion_Logistics_Readonly',
    'Orion_SCM_Readonly',
    'Orion_Finance_Analyst',
    'Orion_Logistics_Slot',
  ];
  public static readonly finantialAccessRoles = ['Orion_Finance_Accountant'];

  public static removeDuplicate(data: string[], nullCheck = true): string[] {
    return Array.from(new Set(nullCheck ? data.filter((e): string => e) : data));
  }

  public static getModifiedPnVc(partNumber: string): PnVendorCode[] {
    let pnVc: PnVendorCode[] = [];
    let partNumbersList = partNumber.split('\n');

    for (let i = 0; i < partNumbersList.length; i++) {
      let pnVendor: PnVendorCode = {};
      partNumbersList[i] = partNumbersList[i].trim();

      if (partNumbersList[i] != '') {
        let splitArr = partNumbersList[i].split('|');

        // checking if pn is present and handling only vc
        if (partNumbersList[i][0] == '|' && splitArr.length == 1) {
          pnVendor.partNumber = '';
          pnVendor.vendorCode = splitArr[0];
        }

        // checking if vc is present and handling only pn
        else if (partNumbersList[i][0] != '' && splitArr.length == 1) {
          pnVendor.partNumber = splitArr[0];
          pnVendor.vendorCode = '';
        } else {
          pnVendor.partNumber = splitArr[0];
          pnVendor.vendorCode = splitArr[1];
        }
        pnVc.push(pnVendor);
      }
    }
    return pnVc;
  }

  public static setValidation(
    control: FormControl,
    isInvalid: boolean,
    errorCode = 'incorrect',
  ): void {
    control.setErrors(isInvalid ? { [errorCode]: true } : null);
  }

  public static handlePastedClipboardData(clipboardData: string): string[] {
    const separatorsRegex = /[\n\r\t|,; :]+/;
    return clipboardData.trim().split(separatorsRegex);
  }
}

export enum EnumDataStatus {
  NONE = 0,
  AVAILABLE = 1,
  FETCHING = 2,
  ERROR = 3,
}
